'use client'

import { getIdToken } from 'firebase/auth'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsLightningChargeFill } from 'react-icons/bs'
import {
  FaAngleDoubleRight,
  FaDownload,
  FaFilm,
  FaGem,
  FaHistory,
  FaImage,
  FaLock,
  FaSave,
  FaTrash,
  FaVideo,
  FaPencilAlt,
  FaTimesCircle,
  FaUserAlt,
  FaSmile,
  FaFilter,
  FaTh,
  FaPalette,
  FaSeedling,
  FaShareAlt,
  FaLongArrowAltRight,
  FaMagic,
  FaTimes,
  FaCheck,
  FaGlobeAmericas,
  FaFolder,
  FaClipboardList,
} from 'react-icons/fa'
import ClipLoader from 'react-spinners/ClipLoader'

import Inputs from '../../../component/Inputs'
import GenerateFeed from '../../../component/GenerateFeed'
import ImageLightbox from '../../../component/ImageLightbox'
import CreateModals from '../../../component/CreateModals'
import GenerateEmptyState from '../../../component/GenerateEmptyState'
import CreateSimplified from './CreateSimplified'
import InputsVideo from '../../../component/InputsVideo'
import Modal from '../../../component/Modal'
import VideoHistoryModal from '../../../component/VideoHistoryModal'
import AnimateModal from '../../../component/AnimateModal'
import TweakModal, { TWEAK_GEM_COST } from '../../../component/TweakModal'
import SignUpComponent from '../../../component/SignUpComponent'
import { setLegacyGenerateUi } from '../../../helpers/generateUiPreference'
import TooltipModal from '../../../component/TooltipModal'

import {
  staticImages,
  staticImagesAnime,
  staticImagesSafe,
} from '../../../config/staticImages'
import { useAuth } from '../../../contexts/AuthContext'
import { useUpgradeGate } from '../../../contexts/UpgradeGateContext'
import { CreateContext, asLiveModel } from '../../../contexts/CreateContext'
import { ImageContext } from '../../../contexts/ImageContext'
import { useSessionFeed } from '../../../contexts/SessionFeedProvider'
import {
  autoDownloadUrls,
  downloadAllImages,
  downloadAllMedia,
  downloadAllVideos,
  downloadImage,
  downloadVideo,
  getAspectRatioNumber,
  isSafeCountry,
  isUrl,
  randomizeSeed,
  saveImage,
  timeAgo,
  toSliderNumber,
  truncate,
  getVideoGemCost,
} from '../../../helpers/helpers'
import {
  VIDEO_V4,
  isLtx,
  normalizeAccent,
  normalizeAspectForVersion,
  normalizeResolution,
  normalizeTone,
  supportsExtend,
  videoVersionFromDoc,
} from '../../../helpers/videoVersions'
import {
  persistImage,
  persistVideo,
  hydrateFeed,
  getDownloadableCached,
  removeCached,
  markCachedSaved,
  sweepExpired,
  MEDIA_TTL_MS,
} from '../../../helpers/mediaCache'
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../../../helpers/safeStorage'
import {
  asResponse,
  collectImageJob,
  forgetJob,
  isConnectionLoss,
  newJobId,
  readPending,
  rememberJob,
} from '../../../lib/image-jobs'
import ImageGrid from '../../../component/ImageGrid'
import * as api from './api'
import {
  countsAsUnsaved,
  offersPublish,
  unsavedPicture,
} from '../../../lib/api/media-ids.ts'
import {
  extendRequest,
  imageRequest,
  tweakRequest,
  videoRequest,
} from './requests'
import {
  bannedWord,
  freeImagesUsedUp,
  hiddenBySafeMode,
  notEnoughGems,
  overloaded,
} from './messages'
import { HiRefresh } from 'react-icons/hi'
import { TbHistoryToggle } from 'react-icons/tb'
import ExtendModal from '../../../component/ExtendModal'
import OverlayActionsMenu, {
  OVERLAY_ICON,
} from '../../../component/OverlayActionsMenu'

// Primary (Animate/Extend) overlay pill. Unlike the shared OVERLAY_PILL, this
// overlay has no @container ancestor, so the label is always shown.
const OVERLAY_PILL =
  'h-11 px-4 rounded-2xl bg-[var(--main-bg-color)] hover:opacity-90 text-white text-sm font-semibold inline-flex items-center gap-2 transition-opacity disabled:opacity-40 disabled:pointer-events-none'
const MAX_PARALLEL_IMAGE_REQUESTS = 2
// Image jobs this page session's own requests are handling (handleSubmit): a Create page
// that mounts again must not collect them a second time. Module scope, so it outlives an
// unmount but not a reload, after which the remembered jobs are collected instead.
const jobsInHand = new Set()
// AI Character v3 adds a heavier "apply" (Qwen) step server-side, so those runs take
// noticeably longer. Give that loader its own longer fake-progress window (so the bar
// keeps creeping toward 90% instead of stalling there) and flip the label to
// "Applying AI Character…" partway through — on a timer, before the result returns —
// so the apply phase reads as part of the wait rather than a late tack-on.
const CHARACTER_LOADER_TOTAL_SECONDS = 24 // fake bar spans this (vs 11 for a plain gen)
const CHARACTER_APPLY_SWITCH_SECONDS = 10 // when the label flips to "Applying AI Character…"
const CHARACTER_MIN_APPLY_SECONDS = 3 // keep that label up ≥ this long if the result beat it

const features = [
  {
    title: 'Enter Your Prompt',
    icon: <FaPencilAlt />,
    content:
      "At the heart of Promptchan AI's Create Page lies the prompt input section. Here, you can enter a prompt to guide the AI in creating your desired image. Whether you're inspired by existing images on the Explore page or utilizing the provided suggestions, your imagination is the only limit. Simply type in your prompt and let the AI work its magic.",
  },
  {
    title: 'Fine-Tuning with Negative Prompts',
    icon: <FaTimesCircle />,
    content:
      "Sometimes, knowing what you don't want is just as important as knowing what you do want. The Negative Prompt feature allows you to specify elements you wish to avoid in your generated image. This powerful tool ensures that the AI's creations align closely with your vision by discouraging unwanted features, perfect for creating unfiltered and unrestricted content.",
  },
  {
    title: 'AI Character and Style Selection',
    icon: <FaUserAlt />,
    content:
      'With AI Character v2.0, your images will always comply with our Terms of Service, ensuring that all creations are AI-generated or artistic works. Choose from various styles, including the cinematic style, to give your image a distinct look and feel. This selection process helps tailor the output to match your artistic intent.',
  },
  {
    title: 'Poses and Emotions',
    icon: <FaSmile />,
    content:
      'Newly introduced pose options enable you to specialize your image generation, adding an extra layer of customization. Coupled with dynamic emotion settings, these features allow you to infuse your images with a wide range of expressions, making them more lifelike and engaging.',
  },
  {
    title: 'Filters and Image Quality',
    icon: <FaFilter />,
    content:
      'Take advantage of the new filter options to apply preset looks to your images, enhancing their aesthetic appeal. You can also select the quality and resolution of your output, ranging from Ultra to Extreme to Max, depending on your need for speed or detail. Whether you prefer a no filter look or a heavily stylized image, the choice is yours.',
  },
  {
    title: 'Aspect Ratio and Image Grid',
    icon: <FaTh />,
    content:
      'Choose the aspect ratio that best suits your project, whether it’s Square, Portrait, or Wide. Additionally, you can select the number of images to generate at once, creating a grid of 1 to 4 images to maximize your creative output.',
  },
  {
    title: 'Creativity and Face Enhancement',
    icon: <FaPalette />,
    content:
      'Adjust the Creativity slider to balance the AI’s adherence to your prompt, whether you prefer a more accurate or a more creative interpretation. Enable the Face Enhance option for more lifelike and detailed results, particularly useful for distant faces in your images. This is especially beneficial when generating unrestricted and no filter content, ensuring a high level of detail and realism.',
  },
  {
    title: 'Seed Control',
    icon: <FaSeedling />,
    content:
      'For those looking to recreate specific results, the Seed feature allows you to enter a seed number or randomize it for new outcomes. Locking the seed can help achieve consistent results across multiple generations, ensuring your unrestricted and unfiltered creations remain true to your vision.',
  },
  {
    title: 'Animate Your Creations',
    icon: <FaFilm />,
    content:
      'Beyond static images, Promptchan AI offers an Animate feature. Enter a prompt and select your desired settings to convert AI-generated images into videos. Adjust camera motion settings such as Still, In, Out, Tilt, Rotate, or Pan to add movement to your shot. This feature is perfect for bringing your no filter content to life.',
  },
  {
    title: 'Publish and Share',
    icon: <FaShareAlt />,
    content:
      "Once you're satisfied with your creation, choose to publish it. Select 'Public' to share images on your public profile, 'Private' to keep them visible only to you, or 'Offline' to stay private. Enable Casual Generations for free, unlimited, slower-speed generations.",
  },
]

const Create = ({ uiVariant } = {}) => {
  const { userImages, setUserImages } = useContext(ImageContext)
  const {
    user,
    subscription,
    gems,
    setGems,
    banned,
    country,
    displayPlan,
    isPlusOrHigher,
    isStudioOrHigher,
  } = useAuth()
  const { openUpgrade, requireFeature } = useUpgradeGate()
  // Variant B's accumulating feed + its bookkeeping live in SessionFeedContext
  // (root-mounted) so the image/video history survives navigating away and back.
  const {
    bFeed,
    setBFeed,
    bIdRef,
    lastImageBatchRef,
    lastVideoBatchRef,
    bPendingIdRef,
    bPendingSourceRef,
    bVideoExtendRef,
  } = useSessionFeed()
  const [timerId, setTimerId] = useState(null)
  const startRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [imageInFlightCount, setImageInFlightCount] = useState(0)
  const imageInFlightCountRef = useRef(0)
  const [toggleSignUp, setToggleSignUp] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  // /api/generate answered 452: the model has no warm worker (nothing was charged).
  // { model, fallback } — internal names; the modal shows them through t().
  const [warmingModel, setWarmingModel] = useState(null)
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false)
  const [earlyAccessModalOpen, setEarlyAccessModalOpen] = useState(false)
  const [, setAlertTitle] = useState('')
  const [, setAlertText] = useState('')

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false)
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false)

  const [cloneLoading, setCloneLoading] = useState(true)
  const [limitTitle, setLimitTitle] = useState(null)
  const [xlLimitModalOpen, setXlLimitModalOpen] = useState(false)
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const [tooltipModalOpen, setTooltipModalOpen] = useState(false)
  const imageRef = useRef()
  const [sessionHistoryVisible, setSessionHistoryVisible] = useState(false)
  const [videoHistoryVisible, setVideoHistoryVisible] = useState(true)
  // animateMode now lives in CreateContext (destructured below) so the Animate modal,
  // the Video tab, and handleSubmitVideoV3 all read one value.
  const [videoQuery, setVideoQuery] = useState([])
  const [videoQueryLoading, setVideoQueryLoading] = useState(false)
  const [refundLoading, setRefundLoading] = useState(false)
  const [extendModalOpen, setExtendModalOpen] = useState(false)
  const [extendSourceIndex, setExtendSourceIndex] = useState(0)
  // Video id handed over from Explore / image detail pages (via localStorage)
  const [extendVideoId, setExtendVideoId] = useState(null)
  // Which engine will extend it. Decided by the SOURCE video, not the Create tab's current
  // version — it gates whether the extend modal offers a spoken line.
  const [extendSourceVersion, setExtendSourceVersion] = useState(VIDEO_V4)
  // Set while the extend modal is open as a Redo of a feed card: { replacesId, selection }.
  // `selection` pre-fills the modal with the choices that card was made with; `replacesId`
  // is the card the new result replaces. Cleared whenever the modal closes.
  const [extendRedo, setExtendRedo] = useState(null)
  const [openMenuIndex, setOpenMenuIndex] = useState(null)
  // Full-screen image viewer src for the legacy gallery (null = closed).
  const [fullscreenImage, setFullscreenImage] = useState(null)
  const [lastVideoModel] = useState()
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [pubFolders, setPubFolders] = useState(['Gallery'])
  const [privFolders, setPrivFolders] = useState(['Gallery'])
  const [selectedFolder, setSelectedFolder] = useState('Gallery')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishIndex, setPublishIndex] = useState()
  const [publishFormat, setPublishFormat] = useState()

  const [showTermsError, setShowTermsError] = useState(false)
  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false)

  // Variant B accumulating result feed (mobile-style stacking, newest on top).
  // Self-contained items so actions/blur are per-item. `bPublishTarget` lets the
  // publish modal target a feed item. The feed itself (`bFeed`) and its dedup/id
  // trackers come from SessionFeedContext (destructured above) so they persist
  // across navigation; dedup is by ARRAY REFERENCE — each generation yields a fresh
  // imageSrc/videoSrc array, processed exactly once.
  const [bPublishTarget, setBPublishTarget] = useState(null)
  // Variant B image-to-video: tapping Animate opens this modal (motion / orientation
  // / quality / audio) instead of firing immediately. `animateSource` is the tapped
  // image {url, isUrl, docRef, model, width?, height?}; confirm → onAnimateConfirm.
  const [animateModalOpen, setAnimateModalOpen] = useState(false)
  const [animateSource, setAnimateSource] = useState(null)
  // Variant B image tweak (Qwen-Edit): tapping Tweak on a result card opens this
  // modal with a prompt box. `tweakSource` is the tapped image {url, isUrl,
  // docRef}; confirm → onTweakConfirm → handleSubmitTweak.
  const [tweakModalOpen, setTweakModalOpen] = useState(false)
  const [tweakSource, setTweakSource] = useState(null)
  // Stable wrapper callbacks for the B feed cards. The real item handlers live in
  // the B render branch (they close over generation logic) so they get a fresh
  // identity each render; these wrappers keep a STABLE identity (created once) and
  // dispatch to the latest handlers via a ref. That lets the memoized feed cards
  // skip re-rendering on every prompt-bar keystroke even with a long feed.
  const bHandlersRef = useRef({})
  // Early access (backend myapp/early_access.py): docRefs of early-access results. Those can't
  // be saved, published, downloaded or made into a character.
  const earlyAccessRef = useRef({})
  const showEarlyAccessModal = () => setEarlyAccessModalOpen(true)
  const earlyAccessModal = (
    <Modal
      isOpen={earlyAccessModalOpen}
      closeModal={() => setEarlyAccessModalOpen(false)}
      title={t('eaTitle', 'Early access')}
      content={
        <p>
          {t(
            'eaSaveBlocked',
            "This model is in early access, so its images can't be saved, published or downloaded yet. They're kept here for 24 hours.",
          )}
        </p>
      }
      confirmation={t('gotIt', 'Got it')}
      onConfirm={() => setEarlyAccessModalOpen(false)}
    />
  )
  const markEarlyAccess = (json) => {
    if (!json?.early_access) return
    ;(json?.doc_refs || []).forEach((ref) => {
      earlyAccessRef.current[ref] = true
    })
  }
  // v2's pictures kept at delivery (a Private order's: create-v2.ts `kept_refs`). Every v2 picture comes as an
  // address, so these are told apart from its unsaved ones here: v1 answered a kept one as an address and an unsaved
  // one as pixels, and Publish is offered for an unsaved one only (media-ids.ts `offersPublish`).
  const keptRef = useRef({})
  // The addresses of v2's unsaved pictures, for the classic page's Save All, offered while one is unsaved (as for
  // v1's pixels): its history keeps addresses alone, no ids.
  const unsavedAddressRef = useRef(new Set())
  const markKept = (json) => {
    const kept = json?.kept_refs || []
    kept.forEach((ref) => {
      keptRef.current[ref] = true
    })
    ;(json?.doc_refs || []).forEach((ref, i) => {
      const url = json?.images?.[i]
      if (
        isUrl(url) &&
        unsavedPicture({ isUrl: true, docRef: ref, kept: kept.includes(ref) })
      )
        unsavedAddressRef.current.add(url)
    })
  }
  const bFeedActionsRef = useRef(null)
  // Build once for stable identities, but REBUILD if a wrapper is missing — e.g. a
  // Fast Refresh that predates a newly-added action (onChat) would otherwise leave
  // the old object in the ref and the new action would silently no-op. Cheap: the
  // memoized cards ignore callback identity, so rebuilding never re-renders them.
  if (!bFeedActionsRef.current || !bFeedActionsRef.current.onClone) {
    bFeedActionsRef.current = {
      onAnimate: (item) => bHandlersRef.current.onAnimateItem?.(item),
      onClone: (item) => bHandlersRef.current.onCloneItem?.(item),
      onTweak: (item) => bHandlersRef.current.onTweakItem?.(item),
      onChat: (item) => bHandlersRef.current.onChatItem?.(item),
      onUseCharacter: (item) => bHandlersRef.current.onUseCharacterItem?.(item),
      onDownload: (item) => bHandlersRef.current.onDownloadItem?.(item),
      onPublish: (item) => bHandlersRef.current.onPublishItem?.(item),
      onSaveWorkflow: (item) => bHandlersRef.current.onSaveWorkflowItem?.(item),
      onDelete: (item) => bHandlersRef.current.onDeleteItem?.(item),
      onExtend: (item) => bHandlersRef.current.onExtendItem?.(item),
      onRedo: (item) => bHandlersRef.current.onRedoItem?.(item),
      onThumb: (item, value) => bHandlersRef.current.onThumbItem?.(item, value),
    }
  }
  // One-tap Animate (mobile-style): the card handler inserts a "pending" item at
  // the top of the feed and seeds the video refs, then bumps this tick; an effect
  // kicks off handleSubmitVideoV3 once the refs have committed (it reads them from
  // state). The pending item lives IN the feed (not pinned above it) so images
  // generated meanwhile prepend above it; on completion it's removed and the
  // finished video is prepended to the very top. `bPendingIdRef` /
  // `bPendingSourceRef` (from SessionFeedContext) track it across navigation.
  const [bAnimateTick, setBAnimateTick] = useState(0)
  // Set on an Explore/image-page "Animate" hand-off so Variant B auto-starts the
  // generation on load (its panel has no Animate button).
  const [pendingAutoAnimate, setPendingAutoAnimate] = useState(false)
  // True while zipping the whole session feed for the "Download all" action, so
  // the button can show a spinner and block re-entry (Variant B only).
  const [bDownloadingAll, setBDownloadingAll] = useState(false)
  // Variant B: the legacy "video progress" panel is now opened on demand from a
  // top-right button (only shown when the user has videos), instead of always
  // being on screen. The grid data is the shared `videoQuery` state below.
  const [bVideoHistoryOpen, setBVideoHistoryOpen] = useState(false)
  // Whether the history has been opened yet (Variant B's panel, or the legacy
  // video tab with its history shown). Until then the list is v2's clips alone:
  // v1's own jobs (chat's scene clips, Telegram's bot) are asked for only once
  // someone opens it, not on every Create load (ai_app_backend plan.pm,
  // r3-video-history).
  const videoHistoryOpenedRef = useRef(false)
  // Bumps whenever new content reaches the TOP of the feed (a placeholder, a new
  // image batch, or a new video) — but NOT on delete. CreateSimplified watches it
  // to play the mobile-style scroll-up reveal.
  const [bRevealNonce, setBRevealNonce] = useState(0)
  // Id of the card the reveal should anchor to: the item that was on top BEFORE the
  // new content landed. CreateSimplified slides that card down out of the way so the
  // new one rises into its place. Defaults to the last-committed top (bPrevTopIdRef),
  // which is the live "previous top" for the direct callers below; the growth effect
  // passes it explicitly since it runs after bPrevTopIdRef has advanced.
  const bRevealAnchorIdRef = useRef(null)
  const requestBFeedReveal = (anchorId) => {
    if (uiVariant !== 'B') return
    bRevealAnchorIdRef.current =
      anchorId !== undefined ? anchorId : bPrevTopIdRef.current
    if (typeof window === 'undefined') {
      setBRevealNonce((n) => n + 1)
      return
    }
    window.requestAnimationFrame(() => {
      setBRevealNonce((n) => n + 1)
    })
  }
  const bPrevFeedLenRef = useRef(0)
  // Tracks the id at the TOP of the feed. Length-growth alone can't detect a new
  // result once the feed is capped (length stays pinned at the max), so we also
  // watch for a fresh item reaching the top.
  const bPrevTopIdRef = useRef(null)
  // False until the first feed-length effect run; lets us adopt a RESTORED feed's
  // length as the baseline on (re)mount without firing the scroll-up reveal.
  const bRevealReadyRef = useRef(false)
  // Coarse clock (updated every 60 s) driving video countdown badges + expiry
  // pruning without per-card timers.
  const [nowTick, setNowTick] = useState(() => Date.now())
  // Guards one-time feed hydration from the short-term cache per uid.
  const bHydratedForRef = useRef(null)
  // Synchronous re-entry guard for video generation. `videoIsLoading` is async
  // state, so two rapid triggers (double-click / two bAnimateTick bumps landing in
  // separate commits) can both pass its check and submit twice. This ref flips
  // synchronously before the first await, so the second call bails immediately.
  const videoSubmitInFlightRef = useRef(false)
  // Id of the feed item whose "Chat" action is mid-flight (publishing the image
  // before handing off to /chat) — drives the per-card spinner.
  const [chatBusyId, setChatBusyId] = useState(null)
  // Id of the feed item whose "Use Character" action is mid-flight (publishing the
  // image before setting it as the AI Character input) — drives its own spinner.
  const [charBusyId, setCharBusyId] = useState(null)
  // Opens the "Character set" confirmation after Use-Character succeeds (mobile
  // parity: the picked face lives in the settings drawer, so confirm it landed).
  const [charSetModalOpen, setCharSetModalOpen] = useState(false)

  // Variant B: results still awaiting a save/publish. Per-item accurate (an item
  // settles when downloaded, published, or deleted — see markItemSaved), unlike
  // the session-global hasUnsavedChanges that Variant A still uses. v1's kept
  // items carry isUrl+docRef and count as already saved; v2's unsaved pictures
  // come as addresses too and count (media-ids.ts `countsAsUnsaved`).
  const bUnsavedCount =
    uiVariant === 'B'
      ? bFeed.filter(
          (it) =>
            it.type !== 'pending' &&
            it.type !== 'image-pending' &&
            it.type !== 'tweak-pending' &&
            !it.saved &&
            countsAsUnsaved(it),
        ).length
      : 0

  // A Redo lasts one opening of the extend modal (its submit has read it): any other opening starts clean.
  useEffect(() => {
    if (!extendModalOpen) setExtendRedo(null)
  }, [extendModalOpen])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const dirty = uiVariant === 'B' ? bUnsavedCount > 0 : hasUnsavedChanges
      if (!dirty) return

      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges, bUnsavedCount, uiVariant])

  useEffect(() => {
    const fetchFolders = async () => {
      if (!user || !publishModalOpen) return

      const uniqueList = (arr) => {
        const seen = new Set()
        const out = []
        ;(arr || []).forEach((v) => {
          const s = String(v || '').trim()
          if (!s) return
          if (seen.has(s)) return
          seen.add(s)
          out.push(s)
        })
        return out
      }

      try {
        const idToken = await getIdToken(user)

        const [pubRes, privRes] = await Promise.all([
          api.publicFolders(idToken),
          api.privateFolders(idToken),
        ])

        const pubJson = pubRes.ok ? await pubRes.json() : { folders: [] }
        const privJson = privRes.ok ? await privRes.json() : { folders: [] }

        // Ensure "Gallery" exists once (pinned first), the rest alphabetical
        const sortRest = (arr) =>
          (arr || [])
            .filter((f) => String(f || '').trim() !== 'Gallery')
            .sort((a, b) =>
              String(a).localeCompare(String(b), undefined, {
                sensitivity: 'base',
              }),
            )
        const pub = uniqueList(['Gallery', ...sortRest(pubJson.folders)])
        const priv = uniqueList(['Gallery', ...sortRest(privJson.folders)])

        setPubFolders(pub)
        setPrivFolders(priv)

        // Keep selection valid: selectedFolder is "pub:Name" or "priv:Name"
        const current = selectedFolder || 'pub:Gallery'
        const [scope, name] = current.split(':')

        const stillValid =
          (scope === 'pub' && pub.includes(name)) ||
          (scope === 'priv' && priv.includes(name))

        if (!stillValid) setSelectedFolder('pub:Gallery')
      } catch (e) {
        console.error('Error fetching folders:', e)
      }
    }

    fetchFolders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, publishModalOpen])

  const publishContent = async () => {
    const current = selectedFolder || 'pub:Gallery'
    const [scope, name] = current.split(':')

    const visibility = scope === 'pub' ? 'Public' : 'Private'
    const folder = name === 'Gallery' ? null : name

    // A clip's ids are videoDocRef's, the pictures' docRef's (CreateContext).
    const publishRefs = publishFormat === 'video' ? videoDocRef : docRef
    const publishData = {
      image:
        publishFormat === 'video'
          ? publishRefs[publishIndex]
          : imageSrc[publishIndex],
      folder,
      visibility,
    }

    try {
      setIsPublishing(true)
      const idToken = await getIdToken(user)

      const response = await api.publish(
        idToken,
        publishFormat === 'video' ? 'video' : 'image',
        publishData,
        publishRefs[publishIndex],
      )

      // A Cloudflare 502/504 is an HTML page: parsing it threw past this try (no catch), and
      // the finally closed the modal with no message, so a paid video looked saved and wasn't.
      const jsonResponse = await response.json().catch(() => ({}))

      if (response.ok) {
        setPublishModalOpen(false)
      } else if (response.status === 403 && jsonResponse?.early_access) {
        setPublishModalOpen(false)
        showEarlyAccessModal()
      } else if (response.status === 403) {
        // NOT an upgrade prompt. This used to say "You must be a Plus or higher to publish",
        // which stopped being true: publish_bp.py gates both /api/publish_image and
        // /api/publish_video on moderation and ownership only — "Publishing is open to all
        // tiers (Free included)". A 403 here now means the request was refused for some other
        // reason, and selling a plan that wouldn't fix it is worse than saying so.
        setPublishModalOpen(false)
        setErrorText(
          <div className="text-center">
            {jsonResponse?.error || 'This image could not be published.'}
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (/21/.test(jsonResponse?.error || '')) {
        // Publishing policy: characters must clearly appear 21+. The age is
        // baked into the already-generated media, so the shortcut raises the
        // Age slider for the next generation rather than "fixing" this one.
        const isVideo = publishFormat === 'video'
        setErrorText(
          <div className="text-center">
            <div className="mb-3">
              To publish, characters must clearly appear 21 or older. Raise the
              Age slider to 21+ and regenerate before publishing.
            </div>
            <button
              onClick={() => {
                if (isVideo) {
                  setShowAttributesVideo(true)
                  setAgeVideo(21)
                } else {
                  setShowAttributes(true)
                  setAge(21)
                }
                setIsErrorModalOpen(false)
              }}
              className="bg-[var(--main-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold transition-colors duration-200"
            >
              Set Age to 21+
            </button>
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (jsonResponse?.slider_reset) {
        // Attribute sliders below the publishable floor. The message stays
        // general — naming the slider and its cutoff would just publish the
        // thresholds — so the recovery is a button rather than an explanation.
        // The backend sends {field: allowed_value}, so the floor lives in one
        // place and this never has to hardcode it.
        const isVideo = publishFormat === 'video'
        const setters = isVideo
          ? {
              weight_slider: setWeightVideo,
              breast_slider: setBreastsVideo,
              ass_slider: setAssVideo,
            }
          : {
              weight_slider: setWeight,
              breast_slider: setBreasts,
              ass_slider: setAss,
            }
        const resets = Object.entries(jsonResponse.slider_reset).filter(
          ([field]) => setters[field],
        )
        setErrorText(
          <div className="text-center">
            <div className="mb-3">
              {jsonResponse.error ||
                'This image does not meet our Community Guidelines. Change your attribute sliders and try again.'}
            </div>
            {resets.length > 0 && (
              <button
                onClick={() => {
                  resets.forEach(([field, value]) => setters[field](value))
                  if (isVideo) setShowAttributesVideo(true)
                  else setShowAttributes(true)
                  setIsErrorModalOpen(false)
                }}
                className="bg-[var(--main-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold transition-colors duration-200"
              >
                Reset attributes
              </button>
            )}
          </div>,
        )
        setIsErrorModalOpen(true)
      } else {
        setErrorText(jsonResponse?.error || 'Failed to publish.')
        setIsErrorModalOpen(true)
      }
    } catch (err) {
      console.error('publish failed', err)
      setErrorText('Failed to publish. Please try again.')
      setIsErrorModalOpen(true)
    } finally {
      setIsPublishing(false)
      setPublishModalOpen(false)
    }
  }

  const {
    model,
    setModel,
    imageSize,
    setImageSize,
    quality,
    setQuality,
    creativity,
    setCreativity,
    prompt,
    setPrompt,
    negativePrompt,
    setNegativePrompt,
    seed,
    setSeed,
    imageSrc,
    setImageSrc,
    isLoading,
    setIsLoading,
    errorText,
    setErrorText,
    lora,
    setLora,
    lorasList,
    setLorasList,
    lorasListVideo,
    setLorasListVideo,
    docRef,
    setDocRef,
    videoDocRef,
    setVideoDocRef,
    isDeleting,
    setIsDeleting,
    locked,
    setLocked,
    ntier,
    setNtier,
    faceFix,
    setFaceFix,
    chillMode,
    setChillMode,
    imageHistory,
    setImageHistory,
    docRefHistory,
    setDocRefHistory,
    safeImage,
    setSafeImage,
    watermark,
    setWatermark,
    cooldown,
    setCooldown,
    cooldownProgress,
    setCooldownProgress,
    initialCooldown,
    setInitialCooldown,
    filter,
    setFilter,
    blurryImage,
    setBlurryImage,
    details,
    setDetails,
    customPoseImage,
    setCustomPoseImage,
    charImage,
    setCharImage,
    charImageIds,
    recentCharacters,
    charImageVideo,
    setCharImageVideo,
    animation,
    setAnimation,
    animateMode,
    setAnimateMode,
    formatTab,
    setFormatTab,
    videoModel,
    setVideoModel,
    videoImageSize,
    setVideoImageSize,
    videoPrompt,
    setVideoPrompt,
    videoNegativePrompt,
    setVideoNegativePrompt,
    videoIsLoading,
    setVideoIsLoading,
    videoLora,
    setVideoLora,
    videoFilter,
    setVideoFilter,
    videoCreativity,
    setVideoCreativity,
    videoSrc,
    setVideoSrc,
    cameraMotion,
    setCameraMotion,
    setLastQuality,
    characterSuggestion,
    setCharacterSuggestion,
    locationSuggestion,
    setLocationSuggestion,
    actionSuggestion,
    setActionSuggestion,
    stickMode,
    setStickMode,
    folderSetting,
    setFolderSetting,
    folders,
    setFolders,
    emotion,
    setEmotion,
    characterVersion,
    setCharacterVersion,
    videoRefImage,
    setVideoRefImage,
    videoRefLora,
    setVideoRefLora,
    videoId,
    setVideoId,
    videoVersion,
    setVideoVersion,
    videoModelMode,
    setVideoModelMode,
    dialogue,
    setDialogue,
    dialogueSpeaker,
    setDialogueSpeaker,
    dialogueTone,
    dialogueAccent,
    setDialogueAccent,
    setDialogueTone,
    videoLength,
    setVideoLength,
    videoFps,
    setVideoFps,
    videoResolution,
    setVideoResolution,
    cameraMove,
    setCameraMove,
    videoQuality,
    setVideoQuality,
    videoImageSizeV2,
    setVideoImageSizeV2,
    videoProgress,
    setVideoProgress,
    age,
    setAge,
    showAttributes,
    setShowAttributes,
    males,
    females,
    showAttributesVideo,
    setShowAttributesVideo,
    weight,
    setWeight,
    breasts,
    setBreasts,
    ass,
    setAss,
    penis,
    setPenis,
    videoFramerateV2,
    setVideoFramerateV2,
    queueLength,
    setQueueLength,
    loop,
    setLoop,
    audioEnabled,
    setAudioEnabled,
    animateVersion,
    setAnimateVersion,
    videoRefId,
    setVideoRefId,
    ageVideo,
    setAgeVideo,
    weightVideo,
    setWeightVideo,
    breastsVideo,
    setBreastsVideo,
    assVideo,
    setAssVideo,
    seedVideo,
    setSeedVideo,
    videoStartingPoint,
    setVideoStartingPoint,
    nsfwEnabled,
    setNsfwEnabled,
    acceptedPublishTerms,
    setAcceptedPublishTerms,
    autoSave,
    setAutoSave,
    visibilityMode,
    setVisibilityMode,
  } = useContext(CreateContext)

  // Shows a message from messages.tsx: its text in the error modal or the limit (upgrade) modal, and the
  // limit modal's title.
  const show = ({ modal, title, body }) => {
    setErrorText(body)
    if (title !== undefined) setLimitTitle(title)
    if (modal === 'limit') setIsLimitModalOpen(true)
    else setIsErrorModalOpen(true)
  }

  const getImageConcurrencyLimit = () =>
    uiVariant === 'B' && !chillMode ? MAX_PARALLEL_IMAGE_REQUESTS : 1
  const imageAtConcurrencyLimit =
    imageInFlightCount >= getImageConcurrencyLimit()
  const beginImageRequest = () => {
    const limit = getImageConcurrencyLimit()
    if (imageInFlightCountRef.current >= limit) return false
    const nextCount = imageInFlightCountRef.current + 1
    imageInFlightCountRef.current = nextCount
    setImageInFlightCount(nextCount)
    setIsLoading(true)
    return true
  }
  const finishImageRequest = () => {
    const nextCount = Math.max(0, imageInFlightCountRef.current - 1)
    imageInFlightCountRef.current = nextCount
    setImageInFlightCount(nextCount)
    if (nextCount === 0) {
      setIsLoading(false)
    }
  }
  const updateGemsFromGeneration = (nextGems) => {
    setGems((current) => {
      if (typeof nextGems !== 'number') return current
      if (typeof current !== 'number') return nextGems
      return Math.min(current, nextGems)
    })
  }

  // Cap the live in-memory feed. Generated images are held as raw base64 in this
  // array (and inline in the DOM), so an unbounded session feed is a slow OOM —
  // the tab crashes and reloads. Kept deliberately low so even a low-end PC (or a
  // phone with many tabs) stays comfortable; ~20 recent results is still a generous
  // scroll-back window. Older results stay in the IndexedDB cache (a reload can
  // surface the newest of them), they're just dropped from RAM.
  const BFEED_MAX_ITEMS = 20
  const capBFeed = (arr) =>
    arr.length > BFEED_MAX_ITEMS ? arr.slice(0, BFEED_MAX_ITEMS) : arr

  // `meta` = the request's model and pose. Thumbs send them for results the backend kept no
  // document for (Free-plan images); otherwise the backend reads its own record.
  // `meta.clone` = the image settings that produced the batch, for the feed's Clone action —
  // unpublished results have no gallery doc to clone from.
  const buildBImageItems = (
    images = [],
    docRefs = [],
    resultBlurry = false,
    meta = null,
  ) =>
    images.map((url, i) => {
      const ref = Array.isArray(docRefs) ? docRefs[i] : undefined
      const ea = ref ? earlyAccessRef.current[ref] : undefined
      return {
        id: ref || `img-${bIdRef.current++}`,
        type: 'image',
        url,
        docRef: ref,
        isUrl: isUrl(url),
        blurry: resultBlurry,
        expiresAt: Date.now() + MEDIA_TTL_MS,
        ...(meta ? { genModel: meta.model, genPose: meta.pose } : {}),
        ...(meta?.clone ? { cloneData: meta.clone } : {}),
        ...(ea ? { earlyAccess: true } : {}),
        ...(ref && keptRef.current[ref] ? { kept: true } : {}),
      }
    })

  // Variant B: renders that were still running when the page last reloaded (or closed):
  // remembered with their job id (src/lib/image-jobs.js), collected from the server and
  // filed in the feed as handleSubmit would have; one the new backend took is followed there
  // by its order id. Errors show their message; a job the server no longer knows (expired, or
  // answered on its own request) is dropped quietly.
  const collectingJobsRef = useRef(new Set())
  const collectPendingJobs = (uid) => {
    const pending = readPending(safeGetItem).filter(
      (job) =>
        (!job.uid || job.uid === uid) &&
        !jobsInHand.has(job.jobId) &&
        !collectingJobsRef.current.has(job.jobId),
    )
    pending.forEach((job) => {
      collectingJobsRef.current.add(job.jobId)
      const placeholderId = `image-pending-job-${job.jobId}`
      setBFeed((prev) =>
        prev.some((it) => it.id === placeholderId)
          ? prev
          : [
              {
                id: placeholderId,
                type: 'image-pending',
                startedAt: job.startedAt,
              },
              ...prev,
            ],
      )
      requestBFeedReveal()
      // A job the new backend took is followed there by its order (noteOrder): the old one never knew it.
      const collecting = job.orderId
        ? getIdToken(user).then((token) => api.recoverOrder(token, job.orderId))
        : collectImageJob({
            apiBase: job.apiBase,
            jobId: job.jobId,
            getToken: () => getIdToken(user),
          })
      collecting
        .then((collected) => {
          const body = collected?.body
          if (collected?.httpStatus === 200 && body?.status === 'success') {
            markKept(body)
            const fresh = buildBImageItems(
              body.images || [],
              body.doc_refs || [],
              false,
              job.meta || null,
            )
            setBFeed((prev) =>
              capBFeed([
                ...fresh,
                ...prev.filter((it) => it.id !== placeholderId),
              ]),
            )
            fresh.forEach((it) => persistImage(uid, it))
            updateGemsFromGeneration(body.gems)
            requestBFeedReveal()
            return
          }
          setBFeed((prev) => prev.filter((it) => it.id !== placeholderId))
          if (collected) {
            setErrorText(
              <div className="text-center">
                {body?.error || t('imageFailedRefunded')}
              </div>,
            )
            setIsErrorModalOpen(true)
          }
        })
        .catch(() =>
          setBFeed((prev) => prev.filter((it) => it.id !== placeholderId)),
        )
        .finally(() => {
          forgetJob(safeGetItem, safeSetItem, job.jobId)
          collectingJobsRef.current.delete(job.jobId)
        })
    })
  }

  // Variant B: accumulate fresh image results into the session feed (newest batch
  // on top). The whole batch is added once, keyed by the imageSrc ARRAY REFERENCE
  // — every generation replaces imageSrc with a brand-new array, so this fires
  // exactly once per generation. (Content keys were unreliable: base64 results
  // share an identical JPEG-header prefix, which collapsed all generations into
  // one item.) Item id = docRef when present, else a monotonic counter (base64 /
  // offline results carry no docRef). No-op for Variant A.
  useEffect(() => {
    if (uiVariant !== 'B') return
    if (!Array.isArray(imageSrc) || imageSrc.length === 0) return
    if (lastImageBatchRef.current === imageSrc) return
    lastImageBatchRef.current = imageSrc
    const fresh = buildBImageItems(imageSrc, docRef, blurryImage)
    setBFeed((prev) => capBFeed([...fresh, ...prev]))
    // Short-term persist so a reload restores these (fire-and-forget).
    if (user?.uid) fresh.forEach((it) => persistImage(user.uid, it))
    // Reveal directly (not only via the length-growth effect): once the feed is at
    // the cap, length stays pinned so that effect alone could miss this batch.
    requestBFeedReveal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc, docRef, uiVariant])

  // Variant B: a finished animate REPLACES its pending placeholder and pops to the
  // very top (above anything generated while it was animating).
  useEffect(() => {
    if (uiVariant !== 'B') return
    if (!Array.isArray(videoSrc) || videoSrc.length === 0) return
    if (lastVideoBatchRef.current === videoSrc) return
    lastVideoBatchRef.current = videoSrc
    const extend = bVideoExtendRef.current
    bVideoExtendRef.current = null
    const fresh = videoSrc.map((url, i) => {
      const ref = Array.isArray(docRef) ? docRef[i] : undefined
      return {
        id: ref || `vid-${bIdRef.current++}`,
        type: 'video',
        url,
        docRef: ref,
        isUrl: isUrl(url),
        blurry: blurryImage,
        // Which model made it: an extension's SOURCE engine, not the tab's version (re-extending offers a line by it).
        videoVersion: extend?.parentVersion || videoVersion,
        // A new scene remembers what it follows in its story (clip-chains.ts); an extension, its parent for Redo.
        ...(extend?.sceneOf && { sceneOf: extend.sceneOf }),
        ...(extend?.parentId && {
          redoOf: {
            parentId: extend.parentId,
            parentVersion: extend.parentVersion,
            selection: extend.selection,
            asClip: extend.asClip, // delivered as its own clip: it folds into a story (src/lib/clip-chains.ts)
          },
        }),
        // 24 h countdown to match the backend temp-URL timeout.
        expiresAt: Date.now() + MEDIA_TTL_MS,
      }
    })
    const pendingId = bPendingIdRef.current
    bPendingIdRef.current = null
    // A Redo's result replaces the card it redid, unless that card was saved or published meanwhile (then it's kept).
    const replacedId =
      extend?.replacesId &&
      bFeed.some((it) => it.id === extend.replacesId && !it.savedStatus)
        ? extend.replacesId
        : null
    setBFeed((prev) => {
      const cleaned =
        pendingId || replacedId
          ? prev.filter((it) => it.id !== pendingId && it.id !== replacedId)
          : prev
      return capBFeed([...fresh, ...cleaned])
    })
    // Short-term persist (URL only) so a reload restores these.
    if (user?.uid) fresh.forEach((it) => persistVideo(user.uid, it))
    if (user?.uid && replacedId) removeCached(user.uid, replacedId)
    requestBFeedReveal()
    // Refresh the video-progress list so its top-right button appears (and its
    // count stays current) as soon as a freshly finished video lands. Use the
    // unguarded fetch so an in-flight load can't make this skip the batch.
    fetchVideoStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSrc, uiVariant])

  // Variant B: drive the in-feed "animating…" placeholder off videoIsLoading.
  // On START, insert it at the top (so images generated meanwhile prepend above
  // it). On END without a produced video (failure/timeout — the error modal is
  // shown by handleSubmitVideoV3), drop it. Success is handled by the videoSrc
  // effect above, which removes it and pops the finished video to the top.
  useEffect(() => {
    if (uiVariant !== 'B') return
    if (videoIsLoading) {
      if (bPendingIdRef.current) return // placeholder already shown
      const src = bPendingSourceRef.current
      const pendingId = `pending-${bIdRef.current++}`
      bPendingIdRef.current = pendingId
      setBFeed((prev) => [
        {
          id: pendingId,
          type: 'pending',
          source: src?.url,
          sourceIsUrl: src?.isUrl,
        },
        ...prev,
      ])
      requestBFeedReveal()
    } else {
      const pendingId = bPendingIdRef.current
      if (!pendingId) return
      bPendingIdRef.current = null
      setBFeed((prev) => prev.filter((it) => it.id !== pendingId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoIsLoading, uiVariant])

  // A failed extend or scene must not tag the next video that lands. After the videoSrc effect: a result and the
  // end of loading arrive in one render, so the tag is read there before it is cleared here.
  useEffect(() => {
    if (!videoIsLoading)
      bVideoExtendRef.current = bPendingSourceRef.current = null
  }, [videoIsLoading])

  // Release the video re-entry guard whenever loading ends, regardless of which
  // exit path (success / failure / gate) cleared videoIsLoading.
  useEffect(() => {
    if (!videoIsLoading) videoSubmitInFlightRef.current = false
  }, [videoIsLoading])

  // Variant B: signal a scroll-up reveal when the feed GROWS (new image at the top
  // or the animate placeholder appearing) — but not on delete. The first run after
  // (re)mount only adopts the baseline, so a feed RESTORED from SessionFeedContext
  // (returning to the page) doesn't trigger the scroll-up animation.
  useEffect(() => {
    if (uiVariant !== 'B') return
    if (!bRevealReadyRef.current) {
      bRevealReadyRef.current = true
      bPrevFeedLenRef.current = bFeed.length
      bPrevTopIdRef.current = bFeed[0]?.id ?? null
      return
    }
    const len = bFeed.length
    const topId = bFeed[0]?.id ?? null
    const grew = len > bPrevFeedLenRef.current
    // Once the feed is capped, a new generation keeps the length pinned at the max,
    // so growth alone misses it. Also reveal when a NEW item reaches the top without
    // the feed shrinking — a delete/expiry shrinks it and must NOT trigger a reveal.
    const newTop =
      topId !== bPrevTopIdRef.current && len >= bPrevFeedLenRef.current
    const prevTop = bPrevTopIdRef.current
    bPrevFeedLenRef.current = len
    bPrevTopIdRef.current = topId
    // Anchor to the card that was on top a moment ago (already overwritten above),
    // so the reveal knows what to slide down as the new one rises in.
    if (grew || newTop) requestBFeedReveal(prevTop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bFeed.length, bFeed[0]?.id, uiVariant])

  // Variant B: on reload restore the feed from the short-term cache. Runs once per
  // uid (after the provider's clear-on-uid reset). Only hydrates when the in-memory
  // feed is empty, so returning to the page (feed already populated) is untouched.
  // Safe against re-adding: on a fresh reload imageSrc/videoSrc are empty, so the
  // batch effects early-return and their dedup refs stay null.
  useEffect(() => {
    if (uiVariant !== 'B') return
    const uid = user?.uid
    if (!uid) return
    if (bHydratedForRef.current === uid) return
    bHydratedForRef.current = uid
    let cancelled = false
    ;(async () => {
      sweepExpired(uid)
      if (bFeed.length === 0) {
        const restored = await hydrateFeed(uid)
        if (cancelled) return
        if (restored.length > 0) {
          // Keep the id counter ahead of any restored monotonic ids so new
          // generations can't collide with a restored `img-N` / `vid-N`.
          const maxN = restored.reduce((m, it) => {
            const match = /^(?:img|vid)-(\d+)$/.exec(it.id || '')
            return match ? Math.max(m, Number(match[1]) + 1) : m
          }, bIdRef.current)
          bIdRef.current = maxN
          setBFeed((prev) => (prev.length > 0 ? prev : capBFeed(restored)))
        }
      }
      // After the feed is restored (it only restores into an empty feed): renders
      // that were still running when the page reloaded.
      if (!cancelled) collectPendingJobs(uid)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, uiVariant])

  // Variant B: a coarse 60 s clock. Each tick refreshes the countdown badges and
  // prunes any result whose 24 h window has elapsed (from the feed and the cache).
  useEffect(() => {
    if (uiVariant !== 'B') return
    const id = setInterval(() => {
      const now = Date.now()
      setNowTick(now)
      const uid = user?.uid
      setBFeed((prev) => {
        const isExpired = (it) => it.expiresAt && it.expiresAt <= now
        const expired = prev.filter(isExpired)
        if (expired.length === 0) return prev
        if (uid) expired.forEach((it) => removeCached(uid, it.id))
        return prev.filter((it) => !isExpired(it))
      })
    }, 60 * 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiVariant, user?.uid])

  // The actual video_status fetch, with NO in-flight guard. Callers that want
  // the spinner / re-entrancy guard go through getVideoQuery; fire-and-forget
  // refreshes (e.g. when a freshly finished video lands) call this directly so a
  // concurrent load can't make them silently no-op and skip the batch.
  const fetchVideoStatus = async () => {
    if (!user) return
    // Set before the first await, so the legacy tab's effect below sees it.
    if (uiVariant !== 'B' && formatTab === 'video' && videoHistoryVisible)
      videoHistoryOpenedRef.current = true
    const withV1 = videoHistoryOpenedRef.current
    try {
      const idToken = await getIdToken(user)

      const response = await api.listVideoJobs(idToken, withV1)

      const jsonResponse = await response.json()

      if (response.ok) {
        // console.log('Query response ok', jsonResponse)
        setVideoQuery(jsonResponse)
      } else {
        console.error('Failed getting the query.')
        setVideoQuery([])
      }
    } catch (error) {
      console.error('Error getting the query:', error)
      setVideoQuery([])
    }
  }

  const getVideoQuery = async () => {
    if (videoQueryLoading || !user) {
      return
    }
    setVideoQueryLoading(true)
    try {
      await fetchVideoStatus()
    } finally {
      setVideoQueryLoading(false)
    }
  }

  useEffect(() => {
    if (videoHistoryVisible && user) {
      getVideoQuery()
    }
  }, [videoHistoryVisible, user])

  // The legacy UI's history is the video tab's list: the first time that tab is
  // shown with its history, ask again with v1's own jobs in it (unguarded, so a
  // load still in flight can't make it skip).
  useEffect(() => {
    if (
      uiVariant !== 'B' &&
      formatTab === 'video' &&
      videoHistoryVisible &&
      user &&
      !videoHistoryOpenedRef.current
    ) {
      fetchVideoStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatTab, videoHistoryVisible, user, uiVariant])

  useEffect(() => {
    const sizeMap = {
      '768x768': '512x512',
      '1024x768': '768x512',
      '768x1024': '512x768',
      '832x832': '512x512',
      '1152x832': '768x512',
      '832x1152': '512x768',
    }

    const updateImageSize = (currentSize) => {
      const newSize = sizeMap[currentSize]
      if (newSize) {
        setImageSize(newSize)
      }
    }

    updateImageSize(imageSize)
  }, [imageSize])

  useEffect(() => {
    if (!user) {
      setImageSrc([])
      setVideoSrc([])
    }
  }, [user])

  // This function is used to start the countdown and update the progress
  useEffect(() => {
    // This function is used to update the cooldown progress in terms of percentage
    const updateProgress = () => {
      let percentage = (cooldown / initialCooldown) * 100
      setCooldownProgress(100 - percentage)
    }

    if (cooldown > 0) {
      const countdown = setInterval(() => {
        setCooldown((prevCooldown) => prevCooldown - 1)
        updateProgress()
      }, 1000)
      return () => clearInterval(countdown)
    }
  }, [cooldown, setCooldown, initialCooldown, setCooldownProgress])

  //watermark off default for all users
  useEffect(() => {
    setWatermark(false)
  }, [setWatermark])

  useEffect(() => {
    if (formatTab === 'video') {
      setChillMode(false)
    }
  }, [formatTab])

  const deleteImage = async (index) => {
    if (!user || !docRef || !docRefHistory) return

    if (isUrl(imageSrc[index])) {
      setIsDeleting(true)

      const idToken = await getIdToken(user)

      try {
        const response = await api.deleteMedia(idToken, {
          // docRef is index-aligned with the VISIBLE batch (imageSrc), which is
          // what the gallery maps over. docRefHistory is the accumulated session
          // history, so docRefHistory[index] points at an older image after the
          // 2nd+ generation — use docRef[index] (mirrors deleteVideo).
          imageId: docRef[index],
          imageUid: user.uid,
        })

        if (response.ok) {
          let newImageSrc = [...imageSrc]
          let newDocRef = [...docRef]
          newImageSrc.splice(index, 1)
          newDocRef.splice(index, 1)
          setImageSrc(newImageSrc)
          setDocRef(newDocRef)

          let newImageHistory = [...imageHistory]
          let newDocRefHistory = [...docRefHistory]
          let reverseIndex =
            newImageHistory.length - 1 - newImageSrc.length + index
          newImageHistory.splice(reverseIndex, 1)
          newDocRefHistory.splice(reverseIndex, 1)
          setImageHistory(newImageHistory)
          setDocRefHistory(newDocRefHistory)
        } else {
          console.error('Failed to delete the image')
        }
      } catch (error) {
        console.error('Error deleting the image:', error)
      } finally {
        setIsDeleting(false)
      }
    } else {
      // If the image is in base64 format, just remove it from the local state
      let newImageSrc = [...imageSrc]
      newImageSrc.splice(index, 1)
      setImageSrc(newImageSrc)

      let newImageHistory = [...imageHistory]
      let reverseIndex = newImageHistory.length - 1 - newImageSrc.length + index
      newImageHistory.splice(reverseIndex, 1)
      setImageHistory(newImageHistory)
    }
  }

  const deleteVideo = async (index) => {
    if (!user) return

    setIsDeleting(true)

    const idToken = await getIdToken(user)

    try {
      // Videos live in the same `images` collection, so /api/delete_image
      // handles them. videoDocRef is index-aligned with videoSrc (set together
      // from the video response), so videoDocRef[index] is the current result's id.
      const response = await api.deleteMedia(idToken, {
        imageId: videoDocRef[index],
        imageUid: user.uid,
      })

      if (response.ok) {
        let newVideoSrc = [...videoSrc]
        let newVideoDocRef = [...videoDocRef]
        newVideoSrc.splice(index, 1)
        newVideoDocRef.splice(index, 1)
        setVideoSrc(newVideoSrc)
        setVideoDocRef(newVideoDocRef)

        let newImageHistory = [...imageHistory]
        let newDocRefHistory = [...docRefHistory]
        let reverseIndex =
          newImageHistory.length - 1 - newVideoSrc.length + index
        newImageHistory.splice(reverseIndex, 1)
        newDocRefHistory.splice(reverseIndex, 1)
        setImageHistory(newImageHistory)
        setDocRefHistory(newDocRefHistory)
      } else {
        console.error('Failed to delete the video')
      }
    } catch (error) {
      console.error('Error deleting the video:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  // The image half of a clone: every setting that shapes a photo, and nothing on the video
  // side. Shared by the cloneData hand-off below and the feed's Clone action, which must not
  // reset the user's video prompt/model the way a full cloneData restore does.
  const applyImageClone = (c) => {
    setModel(asLiveModel.decode(c.model || 'Pony Anime XL'))
    setPrompt(c.prompt || '')
    setNegativePrompt(c.negativePrompt || '')
    setLora(c.loras || 'Default')
    setLorasList(c.lorasList || [])
    setFilter(c.filter || 'Default')
    setCreativity(c.creativity || 50)
    setDetails(c.details || 0)
    setEmotion(c.emotion || 'Default')

    if (subscription && subscription.plan !== 'Free') {
      const aspect = c?.aspect
        ? getAspectRatioNumber(c?.aspect)
        : c.imageSize
          ? c.imageSize
          : '512x512'
      setImageSize(aspect)
      setSeed(c.seed || 1)
    }

    if (c.age_slider) {
      setAge(toSliderNumber(c.age_slider))
      setWeight(toSliderNumber(c.weight_slider) || 0)
      setBreasts(toSliderNumber(c.breast_slider) || 0)
      setAss(toSliderNumber(c.ass_slider) || 0)
      setPenis(toSliderNumber(c.penis_slider) || 0)

      setShowAttributes(true)
    } else {
      setShowAttributes(false)
    }
  }

  useEffect(() => {
    const fetchCloneData = async () => {
      setCloneLoading(true)

      const storedCloneData = safeGetItem('cloneData')
      if (storedCloneData) {
        const parsedCloneData = JSON.parse(storedCloneData)

        // First, so the video sliders below still win when both are present.
        applyImageClone(parsedCloneData)
        setFormatTab(parsedCloneData.formatTab || 'photo')
        // Flag the "Animate" hand-off so Variant B auto-starts once cloneData commits.
        if (
          parsedCloneData.autoAnimate &&
          parsedCloneData.formatTab === 'video'
        ) {
          setPendingAutoAnimate(true)
        }
        setVideoPrompt(parsedCloneData.videoPrompt || '')
        setVideoNegativePrompt(parsedCloneData.videoNegativePrompt || '')
        setVideoModel(parsedCloneData.videoModel || 'Real')
        setVideoLora(parsedCloneData.videoLora || 'Default')
        setVideoImageSize(parsedCloneData.videoImageSize || '512x512')

        setCameraMotion(parsedCloneData.cameraMotion || 'still')
        setVideoId(parsedCloneData.videoId || null)
        setVideoFramerateV2(parsedCloneData.videoFramerateV2 || 25)
        setLoop(parsedCloneData.loop || 'Repeat')

        // Cloning a video restores the model it was made with, plus (v5 only) the settings
        // that are unique to it. Guarded on the value being present so the ordinary
        // clone-an-image path never clobbers the user's current video settings.
        if (parsedCloneData.videoVersion) {
          const cloned = videoVersionFromDoc({
            video_version: parsedCloneData.videoVersion,
            version: parsedCloneData.videoVersion,
          })
          setVideoVersion(cloned)
          if (parsedCloneData.aspect) {
            setVideoImageSizeV2(
              normalizeAspectForVersion(parsedCloneData.aspect, cloned),
            )
          }
          if (isLtx(cloned)) {
            setDialogue(parsedCloneData.dialogue || '')
            if (parsedCloneData.dialogueSpeaker) {
              setDialogueSpeaker(parsedCloneData.dialogueSpeaker)
            }
            // Normalized on the way in: a video cloned from before the preset cut carries a
            // retired tone ('cute', 'anime'), and a value with no matching <option> renders the
            // select blank. Folds to the survivor exactly as the backend does.
            if (parsedCloneData.dialogueAccent) {
              setDialogueAccent(normalizeAccent(parsedCloneData.dialogueAccent))
            }
            if (parsedCloneData.dialogueTone) {
              setDialogueTone(normalizeTone(parsedCloneData.dialogueTone))
            }
            if (parsedCloneData.videoLength) {
              setVideoLength(Number(parsedCloneData.videoLength))
            }
            if (parsedCloneData.videoFps) {
              setVideoFps(Number(parsedCloneData.videoFps))
            }
            // Normalised rather than trusted: clones of clips made before this field existed
            // carry nothing, and must land on the tier they were actually rendered at.
            setVideoResolution(
              normalizeResolution(parsedCloneData.videoResolution),
            )
            setCameraMove(parsedCloneData.cameraMove || '')
          }
        }
        // videoQuality is owned by CreateContext (defaults High, no Standard
        // restore), so it's intentionally not read from cloneData here.
        if (parsedCloneData.videoRefImage) {
          setVideoRefImage(parsedCloneData.videoRefImage || null)
          setVideoRefId(parsedCloneData.videoRefId || null)
        }

        if (parsedCloneData.age_slider_video) {
          setAge(toSliderNumber(parsedCloneData.age_slider_video))
          setWeight(toSliderNumber(parsedCloneData.weight_slider_video) || 0)
          setBreasts(toSliderNumber(parsedCloneData.breast_slider_video) || 0)
          setAss(toSliderNumber(parsedCloneData.ass_slider_video) || 0)

          setShowAttributesVideo(true)
        } else {
          setShowAttributesVideo(false)
        }
      } else {
        setModel('Pony Anime XL')
      }

      setCloneLoading(false)
    }

    fetchCloneData()
  }, [])

  // Extend handed over from Explore / image detail pages: open the pose picker
  // for that video (formatTab/videoModel are restored from cloneData above).
  useEffect(() => {
    const storedExtendId = safeGetItem('extendVideoId')
    if (!storedExtendId) return
    const storedExtendVersion = safeGetItem('extendVideoVersion')
    safeRemoveItem('extendVideoId')
    safeRemoveItem('extendVideoVersion')
    setExtendVideoId(storedExtendId)
    // Older hand-offs carry no version; every video that predates v5 is v4.
    setExtendSourceVersion(storedExtendVersion || VIDEO_V4)
    setExtendModalOpen(true)
  }, [])

  // Tweak handed over from gallery cards / the image detail page: open the tweak
  // modal seeded with that image. isUrl+docRef makes handleSubmitTweak send
  // image_id, which the backend resolves for any public gallery image (and the
  // user's own private ones). Variant A has no tweak UI, so the hand-off is
  // discarded there. Key is removed up-front so back-navigation / StrictMode
  // re-runs never re-open the modal.
  useEffect(() => {
    const stored = safeGetItem('tweakData')
    if (!stored) return
    safeRemoveItem('tweakData')
    if (uiVariant !== 'B') return
    let parsed
    try {
      parsed = JSON.parse(stored)
    } catch {
      return
    }
    if (!parsed?.imageId || !parsed?.imageUrl) return
    setTweakSource({
      url: parsed.imageUrl,
      isUrl: true,
      docRef: parsed.imageId,
    })
    setTweakModalOpen(true)
  }, [uiVariant])

  useEffect(() => {
    if (cloneLoading) return
    safeSetItem(
      'cloneData',
      JSON.stringify({
        model: model ?? model,
        imageSize: imageSize ?? imageSize,
        creativity: creativity || 50,
        prompt: prompt ?? prompt,
        negativePrompt: negativePrompt ?? negativePrompt,
        loras: lora ?? lora,
        lorasList: lorasList ?? lorasList,
        seed: seed ?? seed,
        filter: filter ?? filter,
        formatTab: formatTab ?? formatTab,
        videoPrompt: videoPrompt ?? videoPrompt,
        videoNegativePrompt: videoNegativePrompt ?? videoNegativePrompt,
        videoModel: videoModel ?? videoModel,
        videoLora: videoLora ?? videoLora,
        videoImageSize: videoImageSize ?? videoImageSize,
        emotion: emotion ?? emotion,
        age_slider: age ?? age,
        videoFramerateV2: videoFramerateV2 ?? videoFramerateV2,
        loop: loop ?? loop,
        // videoQuality omitted — owned by CreateContext (Standard never persisted).
      }),
    )
  }, [
    model,
    prompt,
    negativePrompt,
    lora,
    lorasList,
    filter,
    creativity,
    imageSize,
    seed,
    cloneLoading,
    formatTab,
    videoPrompt,
    videoNegativePrompt,
    videoModel,
    videoLora,
    videoImageSize,
    emotion,
    age,
    videoFramerateV2,
    loop,
  ])

  // Non-auth demo reveal: after a realistic delay, show a random blurred sample image
  // and prompt sign-up. isLoading is held true across the delay (caller sets it) so the
  // prompt bar stays in its loading state — spinner shown, generate disabled — until the
  // reveal clears it. This is what keeps the loading indicator honest and blocks spam.
  const revealDemoImage = () => {
    const delay = Math.random() * 2000 + 2000
    setTimeout(() => {
      const combinedImages = [
        ...staticImagesAnime,
        ...staticImages,
        ...staticImagesSafe,
      ]
      const filteredImages = combinedImages.filter((image) => !image.video)

      if (filteredImages.length === 0) {
        console.error('No suitable images found.')
        setIsLoading(false)
        return
      }

      const randomIndex = Math.floor(Math.random() * filteredImages.length)
      const randomImageObj = filteredImages[randomIndex]

      setBlurryImage(true)
      setImageSrc([randomImageObj.imageUrl])
      setIsLoading(false)

      // Let the guest see the teased result, then bring up the sign-up gate.
      setTimeout(() => setToggleSignUp(true), 1200)
    }, delay)
  }

  // The picker shows models by their translated names (ModelModal renders
  // t(title.toLowerCase())); anything that names a model to the user goes through this.
  const modelLabel = (name) => (name ? t(String(name).toLowerCase()) : '')

  const handleSubmit = async (modelOverride) => {
    // A string here comes from the warming modal's "use partner model" button and
    // generates with that model right away, without waiting for the picker state.
    const activeModel =
      typeof modelOverride === 'string' ? modelOverride : model
    if (!user) {
      if (isLoading) return
      setIsLoading(true)
      revealDemoImage()
      return
    }

    if (imageInFlightCountRef.current >= getImageConcurrencyLimit()) return

    let newSeed = null

    if (!locked) {
      newSeed = randomizeSeed(setSeed)
    }

    if (lora === 'Custom' && !customPoseImage) {
      setLora('Default')
    }

    if (!isPlusOrHigher && gems <= 0) {
      show(freeImagesUsedUp(t, { video: true }))

      return
    }

    if (!beginImageRequest()) return
    setBusy(false)
    setErrorText('')
    const imagePendingId =
      uiVariant === 'B' ? `image-pending-${bIdRef.current++}` : null
    // AI Character v3 is the heavier, longer path — the loader gets the extended
    // window + the mid-wait "Applying AI Character…" label swap (see below).
    const applyingCharacter = !!(charImage && characterVersion === 'v3')
    const pendingStartedAt = Date.now()
    let imagePendingSettled = false
    const removeImagePending = () => {
      if (!imagePendingId) return
      setBFeed((prev) => prev.filter((it) => it.id !== imagePendingId))
    }
    const insertGeneratedImages = (images = [], docRefs = [], resultBlurry) => {
      if (!imagePendingId) return
      const fresh = buildBImageItems(images, docRefs, resultBlurry, resultMeta)
      imagePendingSettled = true
      setBFeed((prev) =>
        capBFeed([...fresh, ...prev.filter((it) => it.id !== imagePendingId)]),
      )
      if (user?.uid) fresh.forEach((it) => persistImage(user.uid, it))
      requestBFeedReveal()
    }
    // The card flips the label to "Applying AI Character…" on a timer at
    // CHARACTER_APPLY_SWITCH_SECONDS, so on a normal (longer) v3 run that phase is
    // already showing when the result returns and we reveal immediately. Only pad if
    // the backend beat the switch, so the apply phase is never skipped outright.
    const revealGeneratedImages = async (images, docRefs, resultBlurry) => {
      if (applyingCharacter && imagePendingId) {
        imagePendingSettled = true // hold the placeholder across any short wait below
        const minShownMs =
          (CHARACTER_APPLY_SWITCH_SECONDS + CHARACTER_MIN_APPLY_SECONDS) * 1000
        const elapsedMs = Date.now() - pendingStartedAt
        if (elapsedMs < minShownMs) {
          await new Promise((resolve) =>
            setTimeout(resolve, minShownMs - elapsedMs),
          )
        }
      }
      insertGeneratedImages(images, docRefs, resultBlurry)
    }

    if (imagePendingId) {
      setBFeed((prev) => [
        {
          id: imagePendingId,
          type: 'image-pending',
          startedAt: pendingStartedAt,
          ...(applyingCharacter
            ? {
                expectedDuration: CHARACTER_LOADER_TOTAL_SECONDS,
                phaseSwitchSeconds: CHARACTER_APPLY_SWITCH_SECONDS,
                secondaryLabel: t(
                  'applyingAiCharacter',
                  'Applying AI Character…',
                ),
              }
            : {}),
        },
        ...prev,
      ])
      requestBFeedReveal()
    }

    // The request, and the model, pose and settings the feed files its result under (requests.ts).
    const { body: inputData, meta: resultMeta } = imageRequest({
      model: activeModel,
      prompt,
      negativePrompt,
      seed: newSeed || seed,
      quality,
      creativity,
      imageSize,
      ntier,
      lora,
      lorasList,
      filter,
      details,
      emotion,
      faceFix,
      chillMode,
      safeImage,
      watermark,
      plan: subscription?.plan,
      language: i18n?.language,
      animation,
      stickMode,
      folderSetting,
      characterVersion,
      charImage,
      charImageIds,
      customPoseImage,
      showAttributes,
      age,
      weight,
      breasts,
      ass,
      penis,
      females,
      males,
      nsfwEnabled,
      visibilityMode,
      jobId: newJobId(),
    })

    try {
      const idToken = await getIdToken(user)

      // Flame and the legacy models are served by the same backend; only the route
      // differs. (Flame went to NEXT_PUBLIC_PROMPTCHAN_MAIN, a separate backend, until
      // the two were merged in Sep 2026.)
      const apiBase = api.imageBackend({
        paid: isPlusOrHigher,
        withCharacter: !!charImage,
      })
      rememberJob(safeGetItem, safeSetItem, {
        jobId: inputData.job_id,
        apiBase,
        uid: user?.uid,
        startedAt: Date.now(),
        meta: resultMeta,
      })
      jobsInHand.add(inputData.job_id)
      const collect = () =>
        collectImageJob({
          apiBase,
          jobId: inputData.job_id,
          getToken: () => getIdToken(user),
        })
      let response
      try {
        response = await api.generateImage(
          apiBase,
          activeModel,
          idToken,
          inputData,
        )
      } catch (error) {
        // The connection went (Cloudflare's 100-s cut lands here too: its page has no
        // CORS header). The render may still be running: ask for its answer, and only
        // say the connection was lost if the server has none.
        if (!isConnectionLoss(error)) throw error
        const collected = await collect()
        if (!collected) throw error
        response = asResponse(collected)
      }
      if (response.status === 524 || response.status === 409) {
        // 524: Cloudflare cut it but passed the answer through; 409: this same render
        // is already running (a resent request). Either way, collect it.
        const collected = await collect()
        if (collected) response = asResponse(collected)
      }

      const jsonResponse = await response.json()

      if (response.ok && jsonResponse.status === 'success') {
        markEarlyAccess(jsonResponse)
        markKept(jsonResponse)
        if (visibilityMode === 'Offline') {
          setHasUnsavedChanges(true)
        }

        setErrorText('')
        updateGemsFromGeneration(jsonResponse?.gems)
        const shouldBlurResult = !isPlusOrHigher && gems === 1
        if (uiVariant === 'B') {
          await revealGeneratedImages(
            jsonResponse?.images || [],
            jsonResponse?.doc_refs || [],
            shouldBlurResult,
          )
        } else {
          setImageSrc(jsonResponse?.images)
          setDocRef(jsonResponse?.doc_refs || [])
        }
        // Early-access images can't be kept, so they are never auto-downloaded either.
        if (autoSave && !jsonResponse?.early_access) {
          autoDownloadUrls(jsonResponse?.images)
        }
        setImageHistory((prevHistory) => [
          ...prevHistory,
          ...jsonResponse?.images,
        ])
        setDocRefHistory((prevHistory) => [
          ...prevHistory,
          ...(jsonResponse?.doc_refs || []),
        ])
        setCooldown(jsonResponse?.cooldown)
        setInitialCooldown(jsonResponse?.cooldown)

        setLastQuality(jsonResponse?.quality)

        // `image_generated` is captured SERVER-SIDE ONLY (generate_bp.py). It used to
        // fire here as well, which double-counted every website generation: the
        // backend copy covers all paths (web + API), while this one silently lost
        // ~15% to ad-blockers and consent opt-outs. Two copies of the same event
        // meant the "images generated" metric read ~1.85x reality, and both were
        // billed. The server payload is a superset of what this one sent.

        if (shouldBlurResult) {
          setBlurryImage(true)
          show(freeImagesUsedUp(t, { video: true }))
        } else {
          setBlurryImage(false)
        }
      } else if (
        response.status === 401 &&
        jsonResponse?.error === 'Invalid Image Origin'
      ) {
        // NOT a dead session. generate_bp.py:88-90 rejects a character reference that isn't on
        // the hosted allowlist with a 401, so an unhosted face (an in-session base64 result, a
        // bare private-bucket path) landed here and was reported as "Session expired. Refresh
        // to continue." — advice that cannot work, on a modal that names the wrong problem.
        // The tweak path at the same status already special-cased this; the image path didn't.
        //
        // charImage is sticky (persisted in CreateContext), so leaving it set means every
        // retry fails identically. Clear it, and say so.
        setCharImage(null)
        setErrorText(
          <div className="text-center">
            That character image can&apos;t be used as a reference. Pick a
            different image from Explore or your gallery, or use one of the
            preset faces.
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (response.status === 401) {
        setIsSessionModalOpen(true)
        return
      } else if ([500].includes(response.status)) {
        // An exception inside the render (the AI Character v3 Qwen apply, a facet error, …).
        // Both routes give the gems back before answering: generate_bp.py's catch-all
        // refunds and re-raises, and Flame's charge_gems_async refunds on any exception.
        // A 500 from before the charge cost nothing, hence "any Gems". (2026-10-02)
        setErrorText(
          <div className="text-center">
            {t('imageFailedRefunded')} {t('reportWithScreenshot')}{' '}
            <a
              href="https://discord.gg/cZuJFFb34e"
              target="_blank"
              rel="noopener noreferrer"
              className=""
            >
              Join Discord
            </a>
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      } else if ([448, 502, 504].includes(response.status)) {
        // The GPU job failed: 448 "Failed to get a successful GPU call" on /api/generate
        // (generate_bp.py refunds, then raises 448), and 502/504 on Flame (generate_flame.py:
        // RunPod error, job not finished in time, or connection error, all inside
        // charge_gems_async, which refunds). These are readable JSON, so they come from our
        // backend: Cloudflare's and Cloud Run's own 502/504 pages carry no CORS header and
        // land in the catch below instead. 448 used to fall through to "servers overloaded"
        // (and, for Free, an upgrade pitch), and Flame's 502 to "Connection lost". (2026-10-02)
        setErrorText(
          <div className="text-center">{t('imageFailedTemporary')}</div>,
        )
        setIsErrorModalOpen(true)
        return
      } else if (response.status === 503) {
        // Flame's "<model> is not available right now" (refused before the charge), or the
        // store briefly paused (app.py). Not a connection problem, which is what this said.
        setErrorText(
          <div className="text-center">{t('modelBrieflyUnavailable')}</div>,
        )
        setIsErrorModalOpen(true)
        return
      } else if (response.status === 402) {
        show(
          notEnoughGems(t, subscription, {
            dailyGems: true,
            upgradePlusAndPremium: true,
          }),
        )
      } else if (response.status === 300) {
        show(bannedWord(t, jsonResponse))
      } else if (response.status === 301) {
        show(hiddenBySafeMode(isSafeCountry()))
      } else if (response.status === 403) {
        // /api/generate 403s for two different reasons and the status alone can't tell them
        // apart — but the body can. The AI Character refusal gets its own modal; a style/pose
        // plan refusal (ensure_plan_has_feature) keeps the generic one, which is right for it.
        // Casual's own refusal ("Casual mode doesn't include AI Character", myapp/casual.py
        // limit()) also names AI Character, and only a Pro user can reach it — matched first,
        // because from 2026-10-01 it sent ~30 Pro users a day to the "upgrade to Pro" modal.
        if (/^Casual mode/i.test(jsonResponse?.error || '')) {
          setErrorText(
            <div className="text-center">
              Casual Generations don't include AI Character. Turn off Casual
              Generations to use your character (it costs Gems), or remove the
              character to keep generating for free.
              <button
                type="button"
                className="mt-3 block mx-auto rounded-md bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-200"
                onClick={() => {
                  setChillMode(false)
                  setIsErrorModalOpen(false)
                }}
              >
                Turn off Casual Generations
              </button>
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (/AI Character/i.test(jsonResponse?.error || '')) {
          openUpgrade('aiCharacter')
        } else {
          setErrorText(
            <div className="text-center">
              {t('thisisapremiummodel')}{' '}
              <Link href="/upgrade" className="underline text-white ">
                {t('upgradeyouraccount')}
              </Link>{' '}
              {t('toenjoyaccesstoallstyles')}
            </div>,
          )
          setIsLimitModalOpen(true)
          setLimitTitle(`🚀 ${t('upgradetounlockallfeatures')}`)
        }
      } else if (response.status === 444) {
        // 444 is the CUSTOM POSE (control) image failing the age scan (generate.py
        // get_control_args, BannedControlImage) — not the character image, which this used to
        // name. Raised after the charge; generate_bp.py refunds it before answering.
        setErrorText(
          <div className="text-center">{t('customPoseImageRefused')}</div>,
        )
        setImageSrc([])
        setCooldown(0)
        setIsErrorModalOpen(true)
      } else if (response.status === 429) {
        // Two casual refusals share 429: the cooldown, and "busy" when no paid-for GPU is idle
        // (backend myapp/casual.py) — that one says so itself.
        const busy = /busy/i.test(jsonResponse?.error || '')
        // When casual is busy on this model but free on an XL one, the backend names it
        // (suggested_model): offer the switch, and don't start a countdown that would block it.
        const suggested = busy ? jsonResponse?.suggested_model : null
        setErrorText(
          <div className="text-center">
            {busy ? (
              <>
                {suggested ? (
                  <>
                    Casual Generations are busy on{' '}
                    {t(model?.toLowerCase(), model)} right now, but available on{' '}
                    {t(suggested.toLowerCase(), suggested)}.
                  </>
                ) : (
                  jsonResponse.error
                )}
                {suggested && (
                  <button
                    type="button"
                    className="mt-3 block mx-auto rounded-md bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-200"
                    onClick={() => {
                      setModel(suggested)
                      setIsErrorModalOpen(false)
                    }}
                  >
                    Switch to {t(suggested.toLowerCase(), suggested)}
                  </button>
                )}
              </>
            ) : (
              <>
                Casual Generations are cooling down. Wait{' '}
                {Math.round(jsonResponse.cooldown)} seconds, or disable Casual
                Generations.
              </>
            )}
          </div>,
        )
        if (!suggested) setCooldown(jsonResponse.cooldown)
        setIsErrorModalOpen(true)
      } else if (response.status === 479) {
        setErrorText(
          <div className="text-center">
            Sorry, the AI could not detect a face in your image. Please try a
            different image with a clear face.
          </div>,
        )
        setIsErrorModalOpen(true)
        setCharImage(null)
      } else if (response.status === 443) {
        setErrorText(<div className="text-center">{t('translateDown')}</div>)
        setIsErrorModalOpen(true)
        setCharImage(null)
      } else if (response.status === 421) {
        setXlLimitModalOpen(true)
      } else if (response.status === 452) {
        // The model keeps no always-on worker and none is warm right now
        // (promptchan-backend myapp/model_fallback.py). Nothing was charged and the
        // backend is already booting it. Offer the always-warm partner model it named;
        // "try again shortly" just closes the modal.
        updateGemsFromGeneration(jsonResponse?.gems)
        setWarmingModel({
          model: jsonResponse?.model || activeModel,
          fallback: jsonResponse?.fallback_model || null,
        })
      } else if (response.status === 496) {
        markKept(jsonResponse) // a v2 order's results kept all the same (create-v2.ts `v1Restricted`)
        setHasUnsavedChanges(true)
        setErrorText(<div className="text-center">{t('weAreUnable')}</div>)
        setIsErrorModalOpen(true)

        updateGemsFromGeneration(jsonResponse?.gems)
        if (uiVariant === 'B') {
          insertGeneratedImages(
            jsonResponse?.images || [],
            jsonResponse?.doc_refs || [],
            false,
          )
        } else {
          setImageSrc(jsonResponse?.images)
          // This batch's ids (none for a refused one), never the previous batch's or a clip's.
          setDocRef(jsonResponse?.doc_refs || [])
        }
        if (autoSave) {
          autoDownloadUrls(jsonResponse?.images)
        }
        setImageHistory((prevHistory) => [
          ...prevHistory,
          ...jsonResponse?.images,
        ])
        setCooldown(jsonResponse?.cooldown)
        setInitialCooldown(jsonResponse?.cooldown)
      } else {
        show(overloaded(t, subscription, { paidTitle: 'Something went wrong' }))
      }
    } catch (error) {
      if (isConnectionLoss(error)) {
        // Also where Cloudflare's 100 s cut-off (524) lands: its page has no CORS header,
        // so fetch rejects. Reached only when the server had no answer to collect for
        // the job. Deliberately says nothing about gems: the request may still be
        // running, and one that succeeds after the cut is charged (its image is kept only
        // for Private visibility). Only a failure is refunded. Safari's "Load failed" is
        // a TypeError like the others, so it lands here too (it used to say "overloaded").
        setErrorText(
          <div className="text-center">{t('connectionLostTryAgain')}</div>,
        )
        setIsErrorModalOpen(true)
      } else {
        show(
          overloaded(t, subscription, { paidTitle: 'Servers are overloaded' }),
        )
      }
    } finally {
      // Answered (or given up on) here: nothing left to collect after a reload.
      forgetJob(safeGetItem, safeSetItem, inputData.job_id)
      jobsInHand.delete(inputData.job_id)
      if (!imagePendingSettled) {
        removeImagePending()
      }
      finishImageRequest()

      if (typeof window !== 'undefined' && window.innerWidth <= 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  // Qwen-Edit "tweak": edit an existing feed image with a text instruction.
  // POSTs to promptchan-backend /api/tweak_image. Each request owns its own
  // in-feed placeholder; on completion the placeholder is removed and the result
  // is inserted at the top of the pile, matching the video/extend request style.
  // The source is sent as a doc id when we have one (private results — lets the
  // backend re-sign the stored file), else raw base64 (offline results, verified
  // server-side by hash), else the URL (validated against allowed origins).
  const handleSubmitTweak = async (source, tweakPrompt) => {
    if (!user || !source || !tweakPrompt?.trim()) return

    if (!isPlusOrHigher && gems < TWEAK_GEM_COST) {
      show(freeImagesUsedUp(t, { video: false }))

      return
    }

    setErrorText('')
    const pendingId = `tweak-pending-${bIdRef.current++}`
    const removePending = () => {
      setBFeed((prev) => prev.filter((it) => it.id !== pendingId))
    }
    const insertTweakResult = (images, docRefs) => {
      const fresh = buildBImageItems(images, docRefs, false)
      setBFeed((prev) =>
        capBFeed([...fresh, ...prev.filter((it) => it.id !== pendingId)]),
      )
      if (user?.uid) fresh.forEach((it) => persistImage(user.uid, it))
      requestBFeedReveal()
    }

    const inputData = tweakRequest({
      prompt: tweakPrompt,
      source,
      visibilityMode,
      folderSetting,
      language: i18n?.language,
    })

    setBFeed((prev) => [
      {
        id: pendingId,
        type: 'tweak-pending',
        source: source.url,
        sourceIsUrl: source.isUrl,
        startedAt: Date.now(),
      },
      ...prev,
    ])
    requestBFeedReveal()

    try {
      const idToken = await getIdToken(user)

      const response = await api.tweakImage(idToken, inputData)

      const jsonResponse = await response.json().catch(() => ({}))

      if (response.ok && jsonResponse.status === 'success') {
        if (visibilityMode === 'Offline') {
          setHasUnsavedChanges(true)
        }

        setErrorText('')
        setGems((current) =>
          typeof current === 'number' && typeof jsonResponse?.gems === 'number'
            ? Math.min(current, jsonResponse.gems)
            : jsonResponse?.gems,
        )
        // An edit of an early-access image is early access too (backend tweak_bp).
        markEarlyAccess(jsonResponse)
        markKept(jsonResponse)
        insertTweakResult(
          jsonResponse?.images || [],
          jsonResponse?.doc_refs || [],
        )
        if (autoSave && !jsonResponse?.early_access) {
          autoDownloadUrls(jsonResponse?.images || [])
        }
        setImageHistory((prevHistory) => [
          ...prevHistory,
          ...(jsonResponse?.images || []),
        ])
        setDocRefHistory((prevHistory) => [
          ...prevHistory,
          ...(jsonResponse?.doc_refs || []),
        ])

        // Captured server-side only (tweak_bp.py) — same double-count as
        // image_generated above. The server payload carries gems_cost,
        // gems_remaining, visibility and source on top of `plan`.

        if ((!subscription || subscription?.plan === 'Free') && gems === 1) {
          setBlurryImage(true)
        } else {
          setBlurryImage(false)
        }
      } else if (
        response.status === 401 &&
        jsonResponse?.error === 'Invalid Image Origin'
      ) {
        removePending()
        setErrorText(
          <div className="text-center">
            This image source cannot be tweaked. Please regenerate or save the
            image, then try again.
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (response.status === 401) {
        removePending()
        setIsSessionModalOpen(true)
        return
      } else if ([500].includes(response.status)) {
        removePending()
        setErrorText(
          <div className="text-center">
            A tweak error has occurred. Please try again, or report this to the
            team with a screenshot of the image and instruction used.
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      } else if ([502, 503, 504].includes(response.status)) {
        removePending()
        setErrorText(
          <div className="text-center">
            Connection lost. Ensure you keep this tab open and try again.
          </div>,
        )
        setIsErrorModalOpen(true)
        return
      } else if (response.status === 402) {
        removePending()
        show(
          notEnoughGems(t, subscription, {
            dailyGems: true,
            upgradePlusAndPremium: false,
          }),
        )
      } else if (response.status === 300) {
        removePending()
        show(bannedWord(t, jsonResponse))
      } else if (response.status === 301) {
        removePending()
        show(hiddenBySafeMode(isSafeCountry()))
      } else if (response.status === 400) {
        removePending()
        setErrorText(
          <div className="text-center">
            {jsonResponse?.error ||
              'We could not start this tweak. Please try a different image or instruction.'}
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (response.status === 410) {
        removePending()
        setErrorText(
          <div className="text-center">
            {jsonResponse?.error ||
              'This image has expired or been removed, please regenerate it to tweak.'}
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (response.status === 448) {
        // TweakFailedException: the edit job failed, timed out or could not be submitted
        // or read back (tweak.py) — never the instruction or the source, which this used to
        // blame. tweak_bp.py refunds TWEAK_GEM_COST before answering. (2026-10-02)
        removePending()
        setErrorText(
          <div className="text-center">{t('tweakFailedTemporary')}</div>,
        )
        setIsErrorModalOpen(true)
      } else if (response.status === 468) {
        removePending()
        setErrorText(
          <div className="text-center">
            We could not load the source image. It may have expired; regenerate
            or save it and try again.
          </div>,
        )
        setIsErrorModalOpen(true)
      } else if (response.status === 443) {
        removePending()
        setErrorText(<div className="text-center">{t('translateDown')}</div>)
        setIsErrorModalOpen(true)
      } else if (response.status === 496) {
        markKept(jsonResponse) // a v2 order's results kept all the same (create-v2.ts `v1Restricted`)
        setHasUnsavedChanges(true)
        setErrorText(<div className="text-center">{t('weAreUnable')}</div>)
        setIsErrorModalOpen(true)

        setGems((current) =>
          typeof current === 'number' && typeof jsonResponse?.gems === 'number'
            ? Math.min(current, jsonResponse.gems)
            : jsonResponse?.gems,
        )
        insertTweakResult(
          jsonResponse?.images || [],
          jsonResponse?.doc_refs || [],
        )
        setImageHistory((prevHistory) => [
          ...prevHistory,
          ...(jsonResponse?.images || []),
        ])
      } else {
        removePending()
        setErrorText(
          <div className="text-center">
            {t('serversarecurrentlyoverloaded')}
          </div>,
        )
        setIsErrorModalOpen(true)
      }
    } catch {
      removePending()
      setErrorText(
        <div className="text-center">{t('connectionLostTryAgain')}</div>,
      )
      setIsErrorModalOpen(true)
    }
  }

  const handleSubmitIp = async () => {
    if (user) return

    // Free anonymous generation (the backend /api/generate_ip endpoint) was removed —
    // low ROI and an abuse surface. Non-auth users now always get a blurred sample image
    // plus the sign-up prompt. isLoading is held true across the delayed reveal so the
    // prompt bar shows its spinner and can't be spammed.
    if (isLoading) return
    setIsLoading(true)
    revealDemoImage()
  }

  const handleSubmitVideoV3 = async (extendOpts = null) => {
    const isExtend = !!(extendOpts && extendOpts.extend)
    if (!user) {
      if (videoIsLoading) return
      setVideoIsLoading(true)

      const delay = Math.random() * 2000 + 2000
      setTimeout(() => {
        const combinedVideos = [
          ...staticImagesAnime,
          ...staticImages,
          ...staticImagesSafe,
        ]
        const filteredVideos = combinedVideos.filter((video) => video.video)

        if (filteredVideos.length === 0) {
          console.error('No suitable videos found.')
          setVideoIsLoading(false)
          return
        }

        const randomIndex = Math.floor(Math.random() * filteredVideos.length)
        const randomVideoObj = filteredVideos[randomIndex]

        setBlurryImage(true)
        setVideoSrc([randomVideoObj.imageUrl])
        setVideoIsLoading(false)

        // Let the guest see the teased result, then bring up the sign-up gate.
        setTimeout(() => setToggleSignUp(true), 1200)
      }, delay)

      return
    }

    // Logged-in path: bail if a submit is already in flight (synchronous, so it
    // catches double-fires that the async videoIsLoading check would miss).
    if (videoSubmitInFlightRef.current) return

    let newSeed = null
    if (!locked) {
      newSeed = randomizeSeed(setSeedVideo)
    }

    if (lora === 'Custom' && !customPoseImage) {
      setLora('Default')
    }

    if (displayPlan && !isStudioOrHigher) {
      // Names the feature and the plan instead of "Upgrade to Continue Generating", which reads
      // as an out-of-gems message for something the plan never included.
      openUpgrade(extendOpts ? 'videoExtend' : 'video')
      return
    }

    if (!isPlusOrHigher && gems <= 0) {
      show(freeImagesUsedUp(t, { video: true }))

      return
    }

    videoSubmitInFlightRef.current = true
    setVideoIsLoading(true)
    setBusy(false)
    setErrorText('')
    // What this submit extends, for the result card's Redo (null for a plain generation).
    bVideoExtendRef.current = isExtend
      ? {
          parentId: extendOpts.extendVideoId,
          parentVersion: extendSourceVersion,
          selection: extendOpts.selection || null,
          replacesId: extendRedo?.replacesId || null,
        }
      : bPendingSourceRef.current?.scene || null

    const inputData = isExtend
      ? extendRequest(extendOpts, {
          plan: subscription?.plan,
          folderSetting,
          safeImage,
          visibilityMode,
        })
      : videoRequest({
          prompt: videoPrompt,
          seed: newSeed || seedVideo,
          plan: subscription?.plan,
          folderSetting,
          safeImage,
          model: videoModel,
          lora: videoLora,
          charImage: charImageVideo,
          charImageIds,
          showAttributes: showAttributesVideo,
          age: ageVideo,
          weight: weightVideo,
          breasts: breastsVideo,
          ass: assVideo,
          imageAge: age,
          frameRate: videoFramerateV2,
          loop,
          refId: videoRefId,
          refImage: videoRefImage,
          refLora: videoRefLora,
          animateMode,
          videoId,
          audioEnabled,
          version: videoVersion,
          quality: videoQuality,
          aspect: videoImageSizeV2,
          dialogue,
          dialogueSpeaker,
          dialogueTone,
          dialogueAccent,
          cameraMove,
          length: videoLength,
          fps: videoFps,
          resolution: videoResolution,
          visibilityMode,
        })

    try {
      const idToken = await getIdToken(user)

      // A full video pool answers 471 before anything is charged (backend #125), and it usually clears within a
      // minute as jobs finish (an LTX job is ~45 s): try again up to three times, 20 s apart, while the placeholder
      // keeps loading, before telling the user. ~83 submits a day were refused outright (4-5 Oct 2026).
      const busyRetryDelaysMs = [20000, 20000, 20000]
      let submitResponse
      let submitJson
      for (let attempt = 0; ; attempt++) {
        submitResponse = await api.submitVideo(
          await getIdToken(user),
          inputData,
        )
        submitJson = await submitResponse.json()
        if (
          submitResponse.status !== 471 ||
          attempt >= busyRetryDelaysMs.length
        )
          break
        setQueueLength(4) // the pool is full: at least four jobs ahead
        await new Promise((resolve) =>
          setTimeout(resolve, busyRetryDelaysMs[attempt]),
        )
      }
      setQueueLength(null)

      if (!submitResponse.ok) {
        if (submitResponse.status === 401) {
          setIsSessionModalOpen(true)
          return
        }
        if (
          submitResponse.status === 422 &&
          ['spoken_line_unavailable', 'settings_unavailable'].includes(
            submitJson?.code,
          )
        ) {
          // Made from v2's own picture or clip with a choice v2 can't take yet (src/lib/api/video-v2.ts,
          // notYetOnV2): refused before anything was charged, never sent to v1, which can't find it.
          setLimitTitle(t('videoNotYetTitle'))
          setErrorText(
            <div className="text-center">
              {submitJson.code === 'spoken_line_unavailable'
                ? t(isExtend ? 'videoLineNotYetClip' : 'videoLineNotYetPicture')
                : t('videoSettingsNotYet')}{' '}
              {t('videoNotYetNothingCharged')}
            </div>,
          )
          setIsErrorModalOpen(true)
          return
        }
        if (submitResponse.status === 402) {
          show(
            notEnoughGems(t, subscription, {
              dailyGems: false,
              upgradePlusAndPremium: true,
            }),
          )
        } else if (submitResponse.status === 429) {
          setErrorText(
            <div className="text-center">
              You're going too fast! Wait {Math.round(submitJson.seconds)}{' '}
              seconds before generating another video.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (submitResponse.status === 471) {
          setErrorText(
            <div className="text-center">
              All video servers are busy right now. Any Gems charged have been
              refunded — please try again in a minute.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (submitResponse.status === 479) {
          // The character photo behind a text-to-video base image has no detectable face (the image endpoint's 479).
          setErrorText(
            <div className="text-center">
              Sorry, the AI could not detect a face in your character&apos;s
              photo. Please try a different photo with a clear face. Your Gems
              have been refunded.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (submitResponse.status === 410) {
          // The source image expired or was removed; the backend refunded and says so in plain words.
          setErrorText(
            <div className="text-center">
              {submitJson?.error ||
                'This image has expired or been removed, please regenerate it to animate.'}
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (submitResponse.status === 503 && submitJson?.retry) {
          // A store switch's pause (backend AreaPaused, a few seconds): video3_service refunds
          // the video and its base image before answering. It is not the voice model being
          // down, which the branch below assumes of every 503.
          setErrorText(
            <div className="text-center">
              Video generation is paused for a few seconds. You haven't lost any
              Gems — please try again in a moment.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (isExtend && submitResponse.status === 503) {
          // Same 503 as a normal v5 submit, but "switch to v4" is meaningless here: an extend's
          // engine is decided by the SOURCE video's version, so there is nothing for the user to
          // change. Say what happened and stop, rather than offering a button that does nothing.
          setLimitTitle('Extend is unavailable right now')
          setErrorText(
            <div className="text-center">
              Extending this video isn't available at the moment. Please try
              again shortly — no Gems were charged.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (submitResponse.status === 503) {
          // The voice model has no pods configured/reachable (LTX_VIDEO_URLS empty). Nothing was
          // charged. Offer the switch rather than leaving the user with a dead Generate button.
          // Names the model the SWITCHER shows — "v5" is an internal value the UI no longer uses.
          setLimitTitle('v4-voice is unavailable right now')
          setErrorText(
            <div className="text-center">
              The v4-voice model is temporarily unavailable. Switch to v4 to
              keep generating — no Gems were charged.
              <br />
              <button
                type="button"
                onClick={() => {
                  setVideoVersion(VIDEO_V4)
                  setIsErrorModalOpen(false)
                }}
                className="mt-3 underline text-purple-300"
              >
                Switch to v4
              </button>
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (!isExtend && submitResponse.status === 400) {
          // v5 validates aspect / camera_move / dialogue_speaker BEFORE charging
          // gems. The UI only offers valid values, so this means the client and the pod image
          // have drifted — say what's wrong instead of "servers overloaded".
          setLimitTitle('Something went wrong')
          setErrorText(
            <div className="text-center">
              {submitJson?.error
                ? `${submitJson.error}. Please adjust your video settings and try again — no Gems were charged.`
                : "We couldn't start this video. Please adjust your settings and try again — no Gems were charged."}
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (submitResponse.status === 300) {
          show(bannedWord(t, submitJson))
        } else if (isExtend && submitResponse.status === 409) {
          // Re-extend depth cap hit (backend MAX_EXTENDS). This previously fell through to the
          // generic "servers overloaded / try again later" branch, which is what made the cap
          // look like a transient error the user could retry past.
          setLimitTitle('Maximum extends reached')
          setErrorText(
            <div className="text-center">
              You've reached the maximum number of extends
              {submitJson?.max_extends
                ? ` (${submitJson.max_extends})`
                : ''}{' '}
              for this video. Start a new video to keep creating — no gems were
              charged.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (isExtend && submitResponse.status === 404) {
          // Source video doc missing (deleted / not owned by this user).
          setLimitTitle('Video unavailable')
          setErrorText(
            <div className="text-center">
              We couldn't find the original video to extend — it may have been
              deleted. Please pick another video. No gems were charged.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (isExtend && submitResponse.status === 403) {
          // Extend is a Pro feature ("Studio" is its backend id). The pre-submit gate normally
          // catches this, but a missing subscription object can let the request through to the
          // backend's 403 — so this had to say "Studio", a plan name that appears nowhere on
          // the upgrade screen. The modal resolves the display name itself.
          openUpgrade('videoExtend')
        } else if (isExtend && submitResponse.status === 400) {
          // Malformed extend request (e.g. missing extend_video_id). Shouldn't happen from the
          // UI, but surface a clear message instead of "servers overloaded".
          setLimitTitle('Something went wrong')
          setErrorText(
            <div className="text-center">
              We couldn't start this extend. Please reopen the video and try
              again. No gems were charged.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else if (isExtend && submitResponse.status >= 500) {
          // Internal error inside the extend submit — the backend refunds any gems it charged.
          setLimitTitle('Something went wrong')
          setErrorText(
            <div className="text-center">
              Something went wrong while starting your extend. Please try again
              in a moment — any gems charged were refunded.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          show(
            overloaded(t, subscription, {
              paidTitle: 'Servers are overloaded',
            }),
          )
        }

        return
      }

      if (submitJson?.visibility_switched_to_offline) {
        // weAreUnable says "This image"; the video's own copy says it can still be saved.
        setErrorText(<div className="text-center">{t('videoKeptUnsaved')}</div>)
        setLimitTitle('Kept Unsaved')
        setIsErrorModalOpen(true)
      }

      const requestId = submitJson.request_id
      if (bVideoExtendRef.current)
        bVideoExtendRef.current.asClip = submitJson.as_clip === true // the server decides
      if (!requestId) {
        setErrorText('No request_id returned from the server.')
        setVideoIsLoading(false)
        return
      }

      // Poll every 4 s for the first 2 minutes (most videos finish by then), every 6 s to 5 minutes, then every 10 s.
      // It was a flat 15 s, which added ~7.5 s to every video on average. The 20-minute deadline outlasts the
      // backend's slowest path (a job left to its fallback watchdog is settled within ~16 minutes).
      const pollStartedAt = Date.now()
      const pollDeadline = pollStartedAt + 20 * 60 * 1000
      const pollDelay = () => {
        const elapsed = Date.now() - pollStartedAt
        return elapsed < 2 * 60 * 1000
          ? 4000
          : elapsed < 5 * 60 * 1000
            ? 6000
            : 10000
      }
      let isComplete = false
      let failed = false
      let failureReason = null

      while (Date.now() < pollDeadline) {
        // A fresh token each round: Firebase returns the cached one and renews it when it is about to expire, so a
        // long wait no longer ends in "Session expired" (the one token taken at submit lasted at most an hour).
        let pollToken = idToken
        try {
          pollToken = await getIdToken(user)
        } catch (error) {
          console.warn('Could not refresh the token for polling:', error)
        }
        try {
          const statusResponse = await api.videoStatus(pollToken, requestId)

          /* ----------  401 handling for status  ---------- */
          if (statusResponse.status === 401) {
            setIsSessionModalOpen(true)
            return
          }

          const statusText = await statusResponse.text()

          if (!statusResponse.ok) {
            console.error('Error fetching status:', statusText)
          } else {
            let statusData = {}
            try {
              statusData = JSON.parse(statusText)
            } catch {
              console.warn('Non-JSON status response:', statusText)
            }

            if (statusData.status === 'Completed') {
              isComplete = true

              break
            } else if (statusData.status === 'Failed') {
              // Terminal failure — stop polling immediately instead of waiting
              // out the 15-minute timeout.
              failed = true
              failureReason = statusData.failure_reason || null

              break
            } else if (
              statusData.status === 'InProgress' ||
              statusData.status === 'Unknown'
            ) {
              // still processing — keep polling
            } else {
              console.warn('Unexpected status:', statusText)
            }
          }
        } catch (error) {
          console.error('Error during status fetch:', error)
        }

        try {
          const statusWithLogsResponse = await api.videoStatusWithLogs(
            pollToken,
            requestId,
          )

          /* ----------  401 handling for status_with_logs  ---------- */
          if (statusWithLogsResponse.status === 401) {
            setIsSessionModalOpen(true)
            return
          }

          const statusWithLogsData = await statusWithLogsResponse.json()

          if (
            statusWithLogsData &&
            statusWithLogsResponse.ok &&
            typeof statusWithLogsData.progress === 'number'
          ) {
            const percentComplete = Math.round(
              statusWithLogsData.progress * 100,
            )
            setVideoProgress(percentComplete)

            if (
              statusWithLogsData?.current_queue_length ||
              statusWithLogsData?.initial_queue_length
            ) {
              setQueueLength(
                statusWithLogsData.current_queue_length ??
                  statusWithLogsData.initial_queue_length,
              )
            } else {
              setQueueLength(null)
            }
          } else {
            console.warn(
              'Unexpected status_with_logs response:',
              statusWithLogsData,
            )
          }
        } catch (error) {
          console.error('Error during status with logs fetch:', error)
        }

        await new Promise((resolve) => setTimeout(resolve, pollDelay()))
      }

      if (failed) {
        // The backend refunds a failed video the moment it fails; its reason is technical, so it goes to the console.
        if (failureReason) console.warn('Video failed:', failureReason)
        setErrorText(
          'Video generation failed. Your Gems have been refunded — please try again.',
        )
        setIsErrorModalOpen(true)
        setVideoIsLoading(false)
        return
      }

      if (!isComplete) {
        // The backend hands slow/cold-start jobs to a fallback recovery process, so the
        // video may still finish and appear in history after this client-side timeout.
        // If it can't recover, the backend refunds the gems when it gives up.
        setErrorText(
          "Your video is taking longer than expected. It keeps going in the background and will appear in your video history. If it can't complete, your Gems are refunded automatically.",
        )
        setIsErrorModalOpen(true)
        setVideoIsLoading(false)
        return
      }

      const resultResponse = await api.videoResult(
        await getIdToken(user),
        requestId,
      )

      /* ----------  401 handling for result  ---------- */
      if (resultResponse.status === 401) {
        setIsSessionModalOpen(true)
        return
      }

      const resultJson = await resultResponse.json()

      if (!resultResponse.ok || resultJson?.error) {
        console.error('Error fetching result:', resultJson?.error)
        setErrorText(`Error fetching result: ${resultJson?.error}.`)
        setLimitTitle('Something went wrong.')

        setIsLimitModalOpen(true)
        setVideoIsLoading(false)
        return
      }

      if (resultJson?.status === 'success') {
        if (visibilityMode === 'Offline') {
          setHasUnsavedChanges(true)
        }

        setErrorText('')
        if (Number.isFinite(resultJson?.gems)) setGems(resultJson.gems) // 0 too (S19)
        setVideoSrc(resultJson?.video || [])
        setImageHistory((prevHistory) => [
          ...prevHistory,
          ...(resultJson?.video || []),
        ])
        // The clip's ids go to videoDocRef, never over the pictures' ids in docRef: the classic page's photo grid
        // reads docRef, and a clip's id there sent the clip as the picture to animate (or to delete). Variant B's
        // feed still takes a finished clip's ids from docRef (its effect on videoSrc).
        setVideoDocRef(resultJson?.doc_refs || [])
        if (uiVariant === 'B') setDocRef(resultJson?.doc_refs || [])
        setDocRefHistory((prevHistory) => [
          ...prevHistory,
          ...(resultJson?.doc_refs || []),
        ])
        setCooldown(resultJson?.cooldown)
        setInitialCooldown(resultJson?.cooldown)

        if ((!subscription || subscription?.plan === 'Free') && gems === 1) {
          setBlurryImage(true)
          setErrorText(
            <div className="text-center">
              <Link href="/upgrade" className="underline text-purple-300">
                {t('upgradeyouraccount')}
              </Link>{' '}
              {t('nowtounlockunlimitedimages')}
            </div>,
          )
          setIsLimitModalOpen(true)
          setLimitTitle(`🚀 ${t('upgradetocontinuegenerating')}`)
        } else {
          setBlurryImage(false)
        }

        const newUserImages = userImages[user.uid]
          ? [...userImages[user.uid]]
          : []
        newUserImages.unshift(
          ...(resultJson?.imageData || []).map((imageData) => ({
            ...imageData,
            title: imageData.prompt,
            description: imageData.prompt,
            imageUrl: imageData.image_url.replace(
              process.env.NEXT_PUBLIC_URL_1,
              process.env.NEXT_PUBLIC_URL_2,
            ),
          })),
        )
        setUserImages({ ...userImages, [user.uid]: newUserImages })
      } else {
        show(
          overloaded(t, subscription, {
            paidTitle: 'Servers are overloaded',
            paidText:
              'Your video is still being processed and should be available in your profile within the next 5 minutes.',
          }),
        )
      }
    } catch (error) {
      console.error(error)
      show(overloaded(t, subscription, { paidTitle: 'Servers are overloaded' }))
    } finally {
      setVideoProgress(0.0)
      // Completion breaks out of the poll loop before status_with_logs can clear
      // the queue position, so reset it here to cover every exit path.
      setQueueLength(null)
      await setVideoIsLoading(false)
      if (typeof window !== 'undefined' && window.innerWidth <= 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  // Variant B: run the image-to-video generation a tick after Animate seeded the
  // refs (so handleSubmitVideoV3 reads the committed videoRefImage/settings). The
  // finished video lands in the feed via the videoSrc accumulation effect.
  useEffect(() => {
    if (uiVariant !== 'B' || bAnimateTick === 0) return
    handleSubmitVideoV3()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bAnimateTick])

  // Variant B: the Explore/image-page "Animate" hand-off OPENS the Animate modal
  // (seeded from the handed-off source + its dimensions) instead of auto-firing, so
  // the user sets motion/orientation before spending gems. Consume the flag only when
  // we actually open, so a still-loading auth/source can't swallow it (re-runs as
  // user/videoRefImage arrive). Variant A keeps its manual button.
  useEffect(() => {
    if (!pendingAutoAnimate || cloneLoading) return
    if (uiVariant !== 'B') {
      setPendingAutoAnimate(false) // legacy keeps its manual Animate button
      return
    }
    if (videoIsLoading) return // an animate is already running
    if (!user || !videoRefImage) return // wait for auth + source to commit
    setPendingAutoAnimate(false) // consume once
    // cloneData.videoImageSize is "WxH" (the source image's real dimensions), so the
    // modal can derive the correct default orientation synchronously.
    const [w, h] = String(videoImageSize || '')
      .split('x')
      .map((n) => parseInt(n, 10))
    setAnimateSource({
      url: videoRefImage,
      isUrl: isUrl(videoRefImage),
      docRef: videoRefId,
      model: videoModel,
      lora: videoRefLora, // preserve the source image's lora from the hand-off
      width: Number.isFinite(w) ? w : undefined,
      height: Number.isFinite(h) ? h : undefined,
    })
    setAnimateModalOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingAutoAnimate,
    cloneLoading,
    uiVariant,
    user,
    videoIsLoading,
    videoRefImage,
  ])

  const refundGems = async (requestId) => {
    console.log(`🚀 Refunding gems for video ${requestId}`)
    try {
      setRefundLoading(true)

      const idToken = await getIdToken(user)
      const response = await api.claimFailedVideo(idToken, requestId)

      const json = await response.json()

      if (response.ok) {
        // success: update the UI with new gem balance
        setGems(json.gems)
        console.log('✅ Gems refunded, new balance:', json.gems)
      } else {
        // handle known error cases
        console.error('❌ Refund failed:', json.error)
        alert(`Could not refund gems: ${json.error}`)
      }
    } catch (err) {
      console.error('🛑 Unexpected error refunding gems', err)
      alert('Unexpected error. Please try again later.')
    } finally {
      setRefundLoading(false)
      getVideoQuery()
    }
  }

  const useAnimate = async (index) => {
    try {
      const modelMap = {
        Real: 'Real',
        Hyperreal: 'Hyperreal',
        Anime: 'Anime',
        Hyperanime: 'Anime',
        'K-Pop': 'Real',
        Fur: 'Hyperreal',
      }

      const mappedModel = modelMap.hasOwnProperty(model)
        ? modelMap[model]
        : model

      let imageData = imageSrc[index]
      setVideoRefImage(imageData)
      setVideoRefId(docRef[index])

      setVideoRefLora(lora)
      setVideoPrompt(prompt)
      setVideoModel(mappedModel)
      setVideoImageSize(imageSize)
      setFormatTab('video')
      setVideoStartingPoint('image')
      setVideoId(imageData?.id)
    } catch {
      // setErrorText(error.message)
    }
  }

  useEffect(() => {
    if (isLoading && startRef.current === null) {
      startRef.current = Date.now()
      const id = setInterval(() => {
        const newElapsed = Date.now() - startRef.current

        if (
          newElapsed > 10000 &&
          !busy &&
          (!subscription || subscription.plan === 'Free')
        ) {
          setBusy(true)
        }
      }, 150)
      setTimerId(id)
    } else if (!isLoading && startRef.current !== null) {
      clearInterval(timerId)
      startRef.current = null
      setTimerId(null)
    }

    return () => {
      clearInterval(timerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, subscription, timerId])

  const savePrompt = async (index) => {
    try {
      if (!user) return
      if (!Array.isArray(docRef) || !docRef[index]) return

      const idToken = await getIdToken(user)

      const res = await api.savePrompt(idToken, docRef[index])

      const json = await res.json()

      if (!res.ok) {
        console.error('Save prompt failed:', json?.error)
        setErrorText(json?.error || 'Failed to save prompt.')
        setIsErrorModalOpen(true)
        return
      }

      setAlertTitle('Prompt saved')
      setAlertText(
        'Your prompt and settings have been saved. Find it again by opening the Bookmark tab.',
      )
      setIsAlertModalOpen(true)
    } catch (err) {
      console.error('Save prompt error:', err)
      setErrorText('Failed to save prompt.')
      setIsErrorModalOpen(true)
    }
  }

  const modal = (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm py-8"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full">
        <SignUpComponent message={t('signUpToCreate')} />
      </div>
    </div>
  )

  const formatToggle = (
    <button
      type="button"
      onClick={() => {
        if (formatTab === 'photo') {
          setFormatTab('video')
        } else {
          setFormatTab('photo')
        }
      }}
      className={`relative flex items-center justify-center w-3/4 mx-auto h-11 rounded-lg bg-[var(--foreground-bg-color)] focus:outline-none hover:bg-[var(--secondary-bg-color)] transition-all duration-100 mb-1`}
    >
      <div
        className={`absolute z-10 flex items-center justify-center w-1/2  ${
          formatTab === 'photo' ? 'right-0' : 'left-0'
        }`}
      >
        {formatTab === 'photo' ? (
          <>
            <FaVideo className="z-30" color="#FFF" />{' '}
            <p className="font-semibold text-white ml-2">
              {t('video')}{' '}
              {/* Primary-colour pill on the Video tab. It read "v5!" until the model switcher
                  stopped exposing version numbers at all — Auto / v4 / v4-voice — at which point
                  the badge advertised a version the UI no longer mentions anywhere. `t('new')`
                  is a real key here (unlike the old `t('v4')`, which had no translation in any
                  locale and rendered the raw key). */}
              <span className="bg-[var(--main-bg-color)] text-white text-xs font-bold px-2 py-1 ml-1 rounded-full tracking-tighter absolute -top-3 -right-3 flex items-center justify-center">
                {t('new')} <BsLightningChargeFill className="ml-0.5" />
              </span>
            </p>
          </>
        ) : (
          <>
            <FaImage className="z-30" color="#FFF" />{' '}
            <p className="font-semibold text-white ml-2">{t('image')}</p>
          </>
        )}
      </div>

      <div
        className={`flex items-center justify-center absolute left-0 transition-all duration-300 transform h-10 w-1/2 rounded-lg bg-white ${
          formatTab === 'photo' ? 'translate-x-0 z-20' : 'translate-x-full z-20'
        }`}
      >
        {formatTab === 'photo' ? (
          <>
            <FaImage className="z-30" />{' '}
            <p className="font-semibold ml-2"> {t('image')}</p>
          </>
        ) : (
          <>
            <FaVideo className="z-30" />
            <p className="font-semibold ml-2">{t('video')}</p>{' '}
          </>
        )}
      </div>
    </button>
  )

  // The 452 "model warming" notice (set by handleSubmit). Rendered by both UIs:
  // until 6 October 2026 it lived only in the legacy (A) render, so the default
  // (B) UI dropped the answer silently and the person saw nothing happen (S19).
  const warmingModal = (
    <Modal
      isOpen={!!warmingModel}
      closeModal={() => setWarmingModel(null)}
      title={t('modelWarmingTitle', {
        model: modelLabel(warmingModel?.model),
      })}
      content={
        <div className="text-center">
          {warmingModel?.fallback
            ? t('modelWarmingBody', {
                model: modelLabel(warmingModel?.model),
                fallback: modelLabel(warmingModel?.fallback),
              })
            : t('modelWarmingBodyNoFallback', {
                model: modelLabel(warmingModel?.model),
              })}
        </div>
      }
      confirmation={
        warmingModel?.fallback
          ? t('useFallbackModel', {
              fallback: modelLabel(warmingModel?.fallback),
            })
          : null
      }
      onConfirm={() => {
        const next = warmingModel?.fallback
        setWarmingModel(null)
        if (!next) return
        setModel(next)
        handleSubmit(next)
      }}
      confirmation2={t('tryAgainShortly')}
      onConfirm2={() => setWarmingModel(null)}
    />
  )

  // Variant B (simplified mobile-style UI). Reuses the real generation handlers +
  // modals (CreateModals); results render via GenerateFeed and the surrounding
  // shell is CreateSimplified. Variant A falls through to the unchanged legacy
  // return below.
  if (uiVariant === 'B') {
    // Item-based feed actions — operate on a specific stacked result (by its
    // docRef/url), so they work on older results too, not just the latest batch.
    // One-tap Animate (image-to-video), like promptchan-mobile: seed the video
    // refs from the source image + the current Animate settings, then kick off
    // generation. A placeholder card streams progress; the result replaces it via
    // the videoSrc feed effect. Plan/gem gating + messaging is owned entirely by
    // handleSubmitVideoV3 (same as Variant A's useAnimate) — no pre-gate here, so
    // Free-with-gems users can animate and the correct upgrade copy is shown.
    const onAnimateItem = (item) => {
      if (!user) {
        router.push('/signup')
        return
      }
      if (videoIsLoading) return // an animate is already running
      // Open the Animate modal seeded with this image — nothing fires (and no gems
      // are spent) until the user confirms motion/orientation/quality/audio. This
      // replaces the old fire-immediately path that burned gems with a stale
      // orientation and the image prompt as the motion prompt.
      // A story's New scene with no picture is described in words: the Video tab's text-to-video, seeded with a Try
      // again's words or, after a prompt video, the Video tab's prompt (it names who the story is about).
      const describe = item.scene && !item.url
      setAnimateSource({
        url: item.url,
        isUrl: item.isUrl,
        docRef: item.docRef,
        model: describe ? videoModel : model,
        scene: item.scene, // a story's New scene: where its result lands (ClipChainCard)
        prompt: describe
          ? (item.scene.sceneOf?.prompt ??
            (videoStartingPoint === 'prompt' ? videoPrompt : ''))
          : undefined,
      })
      setAnimateModalOpen(true)
    }

    // Commit the modal's choices in ONE synchronous batch, then bump the tick. Because
    // every setter (incl. videoImageSizeV2) commits before the bAnimateTick effect
    // runs, handleSubmitVideoV3 reads the chosen orientation — no async onload race.
    // Plan/gem gating stays entirely in handleSubmitVideoV3 (no pre-gate here).
    const onAnimateConfirm = (opts) => {
      const src = animateSource
      if (!src) return
      if (!src.url) return onDescribeSceneConfirm(src, opts)
      const modelMap = {
        Real: 'Real',
        Hyperreal: 'Hyperreal',
        Anime: 'Anime',
        Hyperanime: 'Anime',
        'K-Pop': 'Real',
        Fur: 'Hyperreal',
      }
      const srcModel = src.model ?? model
      setVideoRefImage(src.url)
      setVideoRefId(src.docRef ?? null)
      // Prefer the source image's own lora (explore/image hand-off carries it); fall
      // back to the current image-tab lora for a just-generated feed card.
      setVideoRefLora(src.lora ?? lora)
      setVideoModel(
        modelMap.hasOwnProperty(srcModel) ? modelMap[srcModel] : srcModel,
      )
      setVideoImageSize(imageSize)
      setVideoImageSizeV2(opts.orientation) // <-- orientation fix
      // The modal owns the model choice for this animate (it has its own switcher), so commit it
      // here in the same batch — handleSubmitVideoV3 reads videoVersion off context.
      //
      // Prefer the MODE. Committing the resolved version instead would force the Video tab off
      // Auto every time someone used the Animate modal, quietly turning a one-off animate into a
      // permanent model pin. The version fallback stays for callers that predate the mode.
      if (opts.videoModelMode) setVideoModelMode(opts.videoModelMode)
      else if (opts.videoVersion) setVideoVersion(opts.videoVersion)
      setVideoQuality(opts.videoQuality)
      setAudioEnabled(opts.audioEnabled)
      setDialogue(opts.dialogue || '')
      if (opts.dialogueSpeaker) setDialogueSpeaker(opts.dialogueSpeaker)
      if (opts.dialogueTone) setDialogueTone(opts.dialogueTone)
      if (opts.dialogueAccent) setDialogueAccent(opts.dialogueAccent)
      setCameraMove(opts.cameraMove || '')
      // v5-only, and only present when the modal was in v5 — a v4 animate leaves the Video tab's
      // length/fps/resolution untouched rather than resetting them to v5 defaults.
      if (opts.videoLength) setVideoLength(opts.videoLength)
      if (opts.videoFps) setVideoFps(opts.videoFps)
      if (opts.videoResolution) setVideoResolution(opts.videoResolution)
      setAnimateMode(opts.animateMode)
      setVideoPrompt(
        opts.animateMode === 'prompt' ? opts.motionPrompt || '' : '',
      )
      setVideoStartingPoint('image')
      setVideoId(null)
      // Seed the placeholder source BEFORE the tick; it's inserted only once the
      // animate actually STARTS (videoIsLoading → true), so a gated early-return in
      // handleSubmitVideoV3 never leaves an orphan placeholder.
      bPendingSourceRef.current = { ...src }
      setBAnimateTick((n) => n + 1)
    }

    // A described New scene: the Video tab's text-to-video (onGenerateVideoText) with the modal's settings, in the
    // story's shape, its result landing in the story with the words kept for its Try again.
    const onDescribeSceneConfirm = (src, opts) => {
      setVideoRefImage(null)
      setVideoRefId(null)
      setVideoImageSizeV2(opts.orientation)
      if (opts.videoModelMode) setVideoModelMode(opts.videoModelMode)
      setVideoQuality(opts.videoQuality)
      setAudioEnabled(opts.audioEnabled)
      setDialogue(opts.dialogue || '')
      if (opts.dialogueSpeaker) setDialogueSpeaker(opts.dialogueSpeaker)
      if (opts.dialogueTone) setDialogueTone(opts.dialogueTone)
      if (opts.dialogueAccent) setDialogueAccent(opts.dialogueAccent)
      setCameraMove(opts.cameraMove || '')
      if (opts.videoLength) setVideoLength(opts.videoLength)
      if (opts.videoFps) setVideoFps(opts.videoFps)
      if (opts.videoResolution) setVideoResolution(opts.videoResolution)
      setVideoPrompt(opts.motionPrompt)
      setVideoStartingPoint('prompt')
      setVideoId(null)
      const { sceneOf } = src.scene
      bPendingSourceRef.current = {
        scene: {
          ...src.scene,
          sceneOf: { ...sceneOf, prompt: opts.motionPrompt },
        },
      }
      setBAnimateTick((n) => n + 1)
    }

    // Clone an unpublished result: load the settings it was generated with back into the
    // prompt bar. Nothing fires. Only items generated since this shipped carry cloneData —
    // the card hides the action otherwise.
    const onCloneItem = (item) => {
      if (!item?.cloneData) return
      applyImageClone(item.cloneData)
      setFormatTab('photo')
    }

    // Tweak (Qwen-Edit): open the prompt modal seeded with this image — nothing
    // fires (and no gems are spent) until the user confirms. Gem/plan messaging is
    // owned by handleSubmitTweak, same as handleSubmit.
    const onTweakItem = (item) => {
      if (!user) {
        router.push('/signup')
        return
      }
      if (!item.isUrl && !item.docRef) {
        setErrorText(
          <div className="text-center">
            Save or regenerate this image on an upgraded plan before tweaking
            it.
          </div>,
        )
        setIsLimitModalOpen(true)
        setLimitTitle('Tweak')
        return
      }
      setTweakSource({
        url: item.url,
        isUrl: item.isUrl,
        docRef: item.docRef,
      })
      setTweakModalOpen(true)
    }

    const onTweakConfirm = ({ prompt: tweakPromptText }) => {
      const src = tweakSource
      if (!src) return
      handleSubmitTweak(src, tweakPromptText)
    }

    // Once an item is saved/published, flag it so the unsaved-progress button can
    // settle and the countdown badge flips to "Saved"/"Published". The cache entry
    // is KEPT (marked saved in place, not removed) so the result stays in the feed
    // — with its badge, across reloads — until the 24 h TTL sweeps it. `status` is
    // 'saved' (downloaded to device) or 'published' (to a gallery); published always
    // wins so an item that's both reads as Published.
    const markItemSaved = (item, status) => {
      if (!item?.id) return
      setBFeed((prev) =>
        prev.map((it) =>
          it.id === item.id
            ? {
                ...it,
                saved: true,
                savedStatus:
                  status === 'published' || it.savedStatus === 'published'
                    ? 'published'
                    : 'saved',
              }
            : it,
        ),
      )
      if (user?.uid) markCachedSaved(user.uid, item.id, status)
    }

    // Early access: one guard for every keep-it action, logged as a blocked attempt (the
    // clearest "I liked this" signal there is). The backend refuses publishing regardless.
    const isEarlyAccessItem = (item) =>
      !!item &&
      (item.earlyAccess || !!earlyAccessRef.current[item.docRef || item.id])
    const measure = async (send, body) => {
      if (!user) return
      try {
        await send(await getIdToken(user), body)
      } catch {
        // measurement never blocks the UI
      }
    }
    const blockEarlyAccess = (item, action) => {
      if (!isEarlyAccessItem(item)) return false
      showEarlyAccessModal()
      if (item.docRef)
        measure(api.reportEarlyAccessBlocked, {
          media_id: item.docRef,
          action,
        })
      return true
    }
    // Thumbs on any output, any model (backend myapp/output_feedback.py). The card shows the
    // choice at once; a vote can be changed.
    const onThumbItem = (item, value) => {
      setBFeed((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, feedback: value } : it)),
      )
      measure(api.sendOutputFeedback, {
        media_id: item.docRef || item.id,
        value,
        model: item.genModel,
        pose: item.genPose,
      })
    }

    const onDownloadItem = (item) => {
      if (blockEarlyAccess(item, 'download')) return
      setHasUnsavedChanges(false)
      if (item.type === 'video') {
        downloadVideo(
          (item.url || '').replace(
            process.env.NEXT_PUBLIC_URL_1,
            process.env.NEXT_PUBLIC_URL_2,
          ),
        )
      } else if (item.isUrl) {
        downloadImage(item.url)
      } else {
        saveImage(`data:image/jpeg;base64,${item.url}`, `image_${item.id}.jpg`)
      }
      markItemSaved(item, 'saved')
    }

    const onSaveWorkflowItem = async (item) => {
      if (!user || !item.docRef) return
      try {
        const idToken = await getIdToken(user)
        const res = await api.savePrompt(idToken, item.docRef)
        const json = await res.json()
        if (!res.ok) {
          setErrorText(json?.error || 'Failed to save prompt.')
          setIsErrorModalOpen(true)
          return
        }
        setAlertTitle('Prompt saved')
        setAlertText(
          'Your prompt and settings have been saved. Find it again by opening the Bookmark tab.',
        )
        setIsAlertModalOpen(true)
      } catch {
        setErrorText('Failed to save prompt.')
        setIsErrorModalOpen(true)
      }
    }

    const onDeleteItem = async (item) => {
      if (item.isUrl && item.docRef && user) {
        setIsDeleting(true)
        try {
          await api.deleteMedia(await getIdToken(user), {
            imageId: item.docRef,
            imageUid: user.uid,
          })
        } catch (e) {
          console.error('Delete failed', e)
        } finally {
          setIsDeleting(false)
        }
      }
      setBFeed((prev) => prev.filter((it) => it.id !== item.id))
      if (user?.uid) removeCached(user.uid, item.id)
    }

    const onPublishItem = (item) => {
      if (blockEarlyAccess(item, 'publish')) return
      setHasUnsavedChanges(false)
      setBPublishTarget(item)
      setPublishFormat(item.type)
      setPublishModalOpen(true)
    }

    // Chat-from-image (mobile parity): host the generation, then hand off to /chat's
    // existing "create character from image" entry — it reads localStorage.chatImage
    // on mount, prefills the portrait and opens the create-character form. Base64
    // results MUST be published Public: a Private publish uploads to the private
    // bucket and returns a bare relative path (save_media.save_image_file), which
    // renders as a broken <img> and is rejected by create_character's
    // allowed_image_origins check — the whole handoff breaks. An already-hosted
    // image is used as-is; one of v2's unsaved pictures comes as a link that goes
    // with it after 24 hours, so it is published Public first too, on v2 by its id
    // (media-ids.ts `unsavedPicture`). publish_image is Plus-gated (403) — surfaced
    // as the limit modal.
    const onChatItem = async (item) => {
      if (blockEarlyAccess(item, 'create_character')) return
      if (!user) {
        router.push('/signup')
        return
      }
      // Publishing is now open to Free users, so publish_image no longer 403s —
      // which used to be what gated this. Chatting from an image is still a Plus
      // feature, so gate it explicitly up-front (parity with onUseCharacterItem).
      // This also closes the already-hosted (isUrl) path that skips publish.
      if (!isPlusOrHigher) {
        // Creating a character from your own image is Plus, but browsing/chatting
        // existing characters is free — so offer that path instead of a dead-end
        // upsell. The button deep-links into /chat's Discover view (openDiscover).
        openUpgrade('chatFromImage', {
          secondary: {
            label: 'Discover characters',
            onClick: () => {
              safeSetItem('openDiscover', '1')
              router.push('/chat')
            },
          },
        })
        return
      }
      if (item.type !== 'image' || chatBusyId) return
      setChatBusyId(item.id)
      try {
        let url = item.url
        if (unsavedPicture(item)) {
          const idToken = await getIdToken(user)
          const res = await api.publish(
            idToken,
            'image',
            { image: item.url, folder: null, visibility: 'Public' },
            item.docRef,
          )
          const json = await res.json().catch(() => ({}))
          if (res.status === 403) {
            openUpgrade('chatFromImage')
            return
          }
          if (!res.ok) {
            setErrorText(json?.error || 'Could not save the image. Try again.')
            setIsErrorModalOpen(true)
            return
          }
          url = json?.image_url || json?.imageUrl || json?.url
        }
        if (!url) {
          setErrorText('Could not save the image. Try again.')
          setIsErrorModalOpen(true)
          return
        }
        // Hand off exactly like the image-detail "Chat" button: chatImage prefills
        // the create-character portrait, chatId links it to the source generation.
        // safeSetItem: a bare setItem throws on blocked-storage iOS Safari AFTER the
        // Plus-gated publish already succeeded — degrade to a plain /chat visit
        // instead of an error + republish loop.
        safeSetItem('chatImage', url)
        if (item.docRef) safeSetItem('chatId', item.docRef)
        // The model it was made with, so the character keeps it (create_character falls back to
        // this when it cannot find the image's document — an unsaved image's can be gone).
        safeSetItem('chatImageModel', item.genModel || '')
        setHasUnsavedChanges(false)
        markItemSaved(item, 'published')
        router.push('/chat')
      } catch (e) {
        console.error('Chat-from-image failed', e)
        setErrorText('Could not save the image. Try again.')
        setIsErrorModalOpen(true)
      } finally {
        setChatBusyId(null)
      }
    }

    // Use-as-character (mobile parity): host the generation, then feed the URL into
    // the AI Character input (`charImage`) so the next generation carries this face.
    // Mirrors onChatItem's publish flow — base64 results MUST be published Public:
    // a Private publish returns a bare private-bucket path that generate rejects
    // ('Invalid Image Origin') and that silently flips visibilityMode to Offline
    // (Inputs' !charImage.includes('http') check). An already-hosted image is
    // used as-is; one of v2's unsaved pictures is published first, as its link
    // (kept as charImage) goes with it after 24 hours. The plan gate is the
    // up-front requireFeature below — publish_image itself no longer 403s on plan.
    const onUseCharacterItem = async (item) => {
      // Using it as a character reference means hosting it (publish_image) — i.e. keeping it.
      if (blockEarlyAccess(item, 'use_character')) return
      if (!user) {
        router.push('/signup')
        return
      }
      if (item.type !== 'image' || charBusyId) return
      // Gate up-front, or a user silently sets a character and gets a misleading success.
      //
      // Gated on Pro, not Plus. The old Plus check dated from when publish_image was itself
      // Plus-gated; it no longer is (publish_bp.py: "Publishing is open to all tiers"), and
      // Plus was never enough to USE the result — generate_bp.py refuses a non-preset
      // reference below Studio/Pro. So a Plus user got all the way to a character set on
      // their next generation, then a 403.
      if (!requireFeature('aiCharacter')) return
      setCharBusyId(item.id)
      try {
        let url = item.url
        if (unsavedPicture(item)) {
          const idToken = await getIdToken(user)
          const res = await api.publish(
            idToken,
            'image',
            { image: item.url, folder: null, visibility: 'Public' },
            item.docRef,
          )
          const json = await res.json().catch(() => ({}))
          if (res.status === 403) {
            // publish_image no longer plan-gates, so this is some other refusal — report it
            // rather than selling a plan that wouldn't change the outcome.
            setErrorText(json?.error || 'Could not save the image. Try again.')
            setIsErrorModalOpen(true)
            return
          }
          if (!res.ok) {
            setErrorText(json?.error || 'Could not save the image. Try again.')
            setIsErrorModalOpen(true)
            return
          }
          url = json?.image_url || json?.imageUrl || json?.url
        }
        if (!url) {
          setErrorText('Could not save the image. Try again.')
          setIsErrorModalOpen(true)
          return
        }
        // Set the AI Character input (the picker thumbnail lives in the settings
        // drawer in Variant B), then confirm it landed.
        setCharImage(url)
        setCharSetModalOpen(true)
        markItemSaved(item, 'published')
      } catch (e) {
        console.error('Use-character-from-image failed', e)
        setErrorText('Could not save the image. Try again.')
        setIsErrorModalOpen(true)
      } finally {
        setCharBusyId(null)
      }
    }

    const onExtendItem = (item) => {
      setExtendVideoId(item.docRef)
      setExtendSourceVersion(item.videoVersion || VIDEO_V4)
      setExtendRedo(null)
      setExtendModalOpen(true)
    }

    // Redo an extension: extend its PARENT again (unchanged by an extend), the modal pre-filled with this card's
    // choices; the result replaces this card. Gems are charged as for any extend.
    const onRedoItem = (item) => {
      if (!item.redoOf?.parentId) return
      setExtendVideoId(item.redoOf.parentId)
      setExtendSourceVersion(item.redoOf.parentVersion || VIDEO_V4)
      setExtendRedo({ replacesId: item.id, selection: item.redoOf.selection })
      setExtendModalOpen(true)
    }

    // Publish the targeted feed item (mirrors publishContent, item-based).
    const bPublishContent = async () => {
      const target = bPublishTarget
      if (!target) return
      const current = selectedFolder || 'pub:Gallery'
      const [scope, name] = current.split(':')
      const visibility = scope === 'pub' ? 'Public' : 'Private'
      const folder = name === 'Gallery' ? null : name
      try {
        setIsPublishing(true)
        const idToken = await getIdToken(user)
        const response = await api.publish(
          idToken,
          target.type === 'video' ? 'video' : 'image',
          {
            image: target.type === 'video' ? target.docRef : target.url,
            folder,
            visibility,
          },
          target.docRef,
        )
        // As publishContent: an HTML error page must not skip the message.
        const jsonResponse = await response.json().catch(() => ({}))
        if (response.ok) {
          markItemSaved(target, 'published')
          setPublishModalOpen(false)
        } else if (response.status === 403 && jsonResponse?.early_access) {
          setPublishModalOpen(false)
          showEarlyAccessModal()
        } else if (response.status === 403) {
          // Not a plan gate: publishing is open to every plan (see publishContent). This told
          // Pro users to get Plus.
          setErrorText(
            <div className="text-center">
              {jsonResponse?.error || 'This could not be published.'}
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          setErrorText(jsonResponse?.error || 'Failed to publish.')
          setIsErrorModalOpen(true)
        }
      } catch (err) {
        console.error('publish failed', err)
        setErrorText('Failed to publish. Please try again.')
        setIsErrorModalOpen(true)
      } finally {
        setIsPublishing(false)
        setPublishModalOpen(false)
      }
    }

    // Point the stable wrappers at this render's handlers, then hand the cards the
    // STABLE wrapper identities so React.memo can keep them mounted across renders.
    bHandlersRef.current = {
      onAnimateItem,
      onCloneItem,
      onTweakItem,
      onChatItem,
      onUseCharacterItem,
      onDownloadItem,
      onPublishItem,
      onSaveWorkflowItem,
      onDeleteItem,
      onExtendItem,
      onRedoItem,
      onThumbItem,
    }
    const bActions = bFeedActionsRef.current

    const galleryEl = (
      <GenerateFeed
        feed={bFeed}
        user={user}
        t={t}
        now={nowTick}
        isDeleting={isDeleting}
        videoIsLoading={videoIsLoading}
        chatBusyId={chatBusyId}
        charBusyId={charBusyId}
        onAnimate={bActions.onAnimate}
        onClone={bActions.onClone}
        onTweak={bActions.onTweak}
        onChat={bActions.onChat}
        onUseCharacter={bActions.onUseCharacter}
        onDownload={bActions.onDownload}
        onPublish={bActions.onPublish}
        onSaveWorkflow={bActions.onSaveWorkflow}
        onDelete={bActions.onDelete}
        onExtend={bActions.onExtend}
        onRedo={bActions.onRedo}
        onThumb={bActions.onThumb}
        animateProgress={videoProgress}
        animateQueueLength={queueLength}
        animateExpectedDuration={
          videoVersion === 'video2' && videoQuality === 'Standard' ? 150 : 200
        }
        emptyState={<GenerateEmptyState />}
      />
    )

    const modalsEl = (
      <CreateModals
        isAlertModalOpen={isAlertModalOpen}
        setIsAlertModalOpen={setIsAlertModalOpen}
        setBookmarkModalOpen={setBookmarkModalOpen}
        isErrorModalOpen={isErrorModalOpen}
        setIsErrorModalOpen={setIsErrorModalOpen}
        errorText={errorText}
        setErrorText={setErrorText}
        charSetModalOpen={charSetModalOpen}
        setCharSetModalOpen={setCharSetModalOpen}
        publishModalOpen={publishModalOpen}
        setPublishModalOpen={setPublishModalOpen}
        isPublishing={isPublishing}
        selectedFolder={selectedFolder}
        setSelectedFolder={setSelectedFolder}
        pubFolders={pubFolders}
        privFolders={privFolders}
        showTermsError={showTermsError}
        setShowTermsError={setShowTermsError}
        acceptedPublishTerms={acceptedPublishTerms}
        setAcceptedPublishTerms={setAcceptedPublishTerms}
        publishContent={bPublishContent}
        isSessionModalOpen={isSessionModalOpen}
        setIsSessionModalOpen={setIsSessionModalOpen}
        user={user}
        isLimitModalOpen={isLimitModalOpen}
        setIsLimitModalOpen={setIsLimitModalOpen}
        limitTitle={limitTitle}
        subscription={subscription}
        router={router}
        xlLimitModalOpen={xlLimitModalOpen}
        setXlLimitModalOpen={setXlLimitModalOpen}
        setModel={setModel}
        tooltipModalOpen={tooltipModalOpen}
        setTooltipModalOpen={setTooltipModalOpen}
        extendModalOpen={extendModalOpen}
        setExtendModalOpen={setExtendModalOpen}
        lastVideoModel={lastVideoModel}
        videoModel={videoModel}
        handleSubmitVideoV3={handleSubmitVideoV3}
        extendVideoId={extendVideoId}
        extendSourceVersion={extendSourceVersion}
        extendInitial={extendRedo?.selection || null}
        docRef={docRef}
        extendSourceIndex={extendSourceIndex}
        t={t}
      />
    )

    // Everything in the session feed the user can actually save — finished images
    // and videos, excluding the in-flight "animating…" placeholder, paywalled
    // (blurred) results, and anything already saved/published (Download All skips
    // those, and the count should reflect what it will actually grab).
    const bDownloadableItems = bFeed.filter(
      (it) =>
        (it.type === 'image' || it.type === 'video') && !it.blurry && !it.saved,
    )

    // Zip the FULL 24 h window of unsaved results — not just what's currently in the
    // capped RAM feed. Pull every non-expired record straight from the cache as
    // Blobs (zipped directly, no base64 inflation) and union with any live-feed item
    // not yet persisted to disk. src is resolved exactly like the per-item download
    // (onDownloadItem): videos (and url-backed images) go through the CDN host swap;
    // base64 images become a data URI. The helper skips any item that fails to fetch.
    const onDownloadAll = async () => {
      if (bDownloadingAll) return
      setBDownloadingAll(true)
      try {
        const cached = user?.uid ? await getDownloadableCached(user.uid) : []
        const seenIds = new Set(cached.map((c) => c.id))
        // Just-generated results may not have finished persisting yet — include any
        // live-feed downloadable not already covered by the cache.
        const liveExtra = bDownloadableItems.filter((it) => !seenIds.has(it.id))
        // Drop any record with no usable source (a corrupt/half-written base64
        // image would otherwise zip as a junk "image").
        const all = [...cached, ...liveExtra].filter(
          (it) =>
            (it.blob instanceof Blob || it.url) &&
            // Early-access results can't be kept — not even in a zip.
            !it.earlyAccess &&
            !earlyAccessRef.current[it.id],
        )
        const swapHost = (url) =>
          (url || '').replace(
            process.env.NEXT_PUBLIC_URL_1,
            process.env.NEXT_PUBLIC_URL_2,
          )
        const items = all.map((it) => ({
          id: it.id,
          type: it.type,
          // Cached images carry a Blob → zip directly; everything else via src.
          blob: it.blob instanceof Blob ? it.blob : undefined,
          src:
            it.blob instanceof Blob
              ? undefined
              : it.type === 'video'
                ? swapHost(it.url)
                : it.isUrl
                  ? it.url
                  : `data:image/jpeg;base64,${it.url}`,
        }))
        if (!items.length) {
          setBDownloadingAll(false)
          return
        }
        const savedIds = all.map((it) => it.id)
        const { added, failed } = await downloadAllMedia(items)
        // Anything that couldn't be read into the zip (typically temp-bucket
        // videos with no CORS) is retried individually — downloadVideo/Image fall
        // back to the native-share / direct-link path that doesn't need CORS.
        failed.forEach((f) => {
          if (f.type === 'video') downloadVideo(f.src)
          else downloadImage(f.src)
        })
        if (added === 0 && failed.length === 0) {
          setErrorText('Could not download your media. Please try again.')
          setIsErrorModalOpen(true)
        } else {
          // Settle the unsaved-progress state. Keep the cache entries (marked saved
          // in place) so they stay in the feed until the TTL; Download All will skip
          // them next time.
          const savedSet = new Set(savedIds)
          setBFeed((prev) =>
            prev.map((it) =>
              savedSet.has(it.id)
                ? {
                    ...it,
                    saved: true,
                    savedStatus:
                      it.savedStatus === 'published' ? 'published' : 'saved',
                  }
                : it,
            ),
          )
          if (user?.uid)
            savedIds.forEach((id) => markCachedSaved(user.uid, id, 'saved'))
        }
      } catch (err) {
        console.error('Download all failed', err)
        setErrorText('Could not download your media. Please try again.')
        setIsErrorModalOpen(true)
      } finally {
        setBDownloadingAll(false)
      }
    }

    const bridge = {
      // Image mode: the prompt bar generates images. Image-to-video is produced via
      // the Animate button on a result card (opens the Animate modal).
      onGenerate: () => (user ? handleSubmit() : handleSubmitIp()),
      imageAtConcurrencyLimit,
      // Video tab: the prompt bar does text-to-video. No source image → the
      // txt2VidData path in handleSubmitVideoV3. videoPrompt is bound to the bar.
      onGenerateVideoText: () => {
        if (!user) {
          router.push('/signup')
          return
        }
        if (videoIsLoading) return
        setVideoRefImage(null)
        setVideoRefId(null)
        setVideoStartingPoint('prompt')
        setVideoId(null)
        bPendingSourceRef.current = null // no source thumbnail for the placeholder
        setBAnimateTick((n) => n + 1)
      },
      // Shown on the video-mode prompt bar (mirrors the InputsVideo cost bar).
      videoGemCost: getVideoGemCost({
        videoQuality,
        videoVersion,
        audioEnabled,
        model: videoModel,
        hasReferenceImage: !!videoRefImage,
        videoLength,
        videoFps,
        videoResolution,
      }),
      handleSubmitVideoV3,
      setIsLimitModalOpen,
      setIsErrorModalOpen,
      setHasUnsavedChanges,
      setBookmarkModalOpen,
      bookmarkModalOpen,
      setExtendModalOpen,
      // Session-wide "Download all" (zip): handler, busy flag, and how many items
      // are saveable so the shell can hide the control when there's nothing to grab.
      onDownloadAll,
      downloadingAll: bDownloadingAll,
      downloadCount: bDownloadableItems.length,
      // How many results still need saving — drives the Save-All button's warning
      // state (amber while > 0, normal once everything is saved/published).
      unsavedCount: bUnsavedCount,
      // Video-progress panel: open on demand (re-fetching the latest jobs), shown
      // via a top-right button only when the user actually has videos in the list.
      onOpenVideoHistory: () => {
        videoHistoryOpenedRef.current = true
        setBVideoHistoryOpen(true)
        getVideoQuery()
      },
      videoCount: videoQuery?.length || 0,
    }

    return (
      <>
        {toggleSignUp && modal}
        {modalsEl}
        {earlyAccessModal}
        {warmingModal}
        <CreateSimplified
          gallery={galleryEl}
          bridge={bridge}
          revealNonce={bRevealNonce}
          revealAnchorId={bRevealAnchorIdRef.current}
          feedCount={
            bFeed.filter((it) => !String(it.type).includes('pending')).length
          }
        />
        <VideoHistoryModal
          isOpen={bVideoHistoryOpen}
          onClose={() => setBVideoHistoryOpen(false)}
          videoQuery={videoQuery}
          videoQueryLoading={videoQueryLoading}
          refundLoading={refundLoading}
          onRefresh={getVideoQuery}
          onRefund={refundGems}
          onSaveAll={() => {
            downloadAllVideos(videoQuery)
            setHasUnsavedChanges(false)
          }}
        />
        <AnimateModal
          isOpen={animateModalOpen}
          closeModal={() => setAnimateModalOpen(false)}
          source={animateSource}
          videoModelMode={videoModelMode}
          videoLength={videoLength}
          videoResolution={videoResolution}
          videoFps={videoFps}
          initial={{
            dialogue,
            dialogueSpeaker,
            dialogueTone,
            dialogueAccent,
            cameraMove,
            // Motion is seeded from the last-used values too, so the modal doesn't drop a
            // typed motion line while restoring a spoken one — see the reset effect there.
            motionPrompt: videoPrompt,
            animateMode,
          }}
          onConfirm={onAnimateConfirm}
        />
        <TweakModal
          isOpen={tweakModalOpen}
          closeModal={() => setTweakModalOpen(false)}
          source={tweakSource}
          onConfirm={onTweakConfirm}
        />
      </>
    )
  }

  return (
    <div className="bg-[var(--background-bg-color)] min-h-screen">
      {toggleSignUp && modal}

      <ImageLightbox
        src={fullscreenImage}
        onClose={() => setFullscreenImage(null)}
      />

      <div
        className={`mx-auto px-3 pt-6 pb-3 ${
          !user && toggleSignUp
            ? 'blur-[2px] pointer-events-none opacity-40'
            : ''
        }`}
      >
        <section className="w-full">
          <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6">
            <div className="w-full order-1 md:hidden">{formatToggle}</div>
            <div className="w-full xl:w-1/4 md:w-1/3 sm:w-full order-3 md:order-1 min-w-0">
              <div className="hidden md:block">{formatToggle}</div>
              {/* Inputs component seems to be causing the react hydration errors */}
              {formatTab === 'photo' ? (
                <Inputs
                  model={model}
                  setModel={setModel}
                  imageSize={imageSize}
                  setImageSize={setImageSize}
                  quality={quality}
                  setQuality={setQuality}
                  creativity={creativity}
                  setCreativity={setCreativity}
                  prompt={prompt}
                  setPrompt={setPrompt}
                  negativePrompt={negativePrompt}
                  setNegativePrompt={setNegativePrompt}
                  seed={seed}
                  setSeed={setSeed}
                  handleSubmit={user ? handleSubmit : handleSubmitIp}
                  subscription={subscription}
                  isLoading={isLoading}
                  lora={lora}
                  setLora={setLora}
                  lorasList={lorasList}
                  setLorasList={setLorasList}
                  locked={locked}
                  setLocked={setLocked}
                  ntier={ntier}
                  setNtier={setNtier}
                  faceFix={faceFix}
                  setFaceFix={setFaceFix}
                  chillMode={chillMode}
                  setChillMode={setChillMode}
                  cooldown={cooldown}
                  cooldownProgress={cooldownProgress}
                  setImageSrc={setImageSrc}
                  setImageHistory={setImageHistory}
                  filter={filter}
                  setFilter={setFilter}
                  details={details}
                  setDetails={setDetails}
                  customPoseImage={customPoseImage}
                  setCustomPoseImage={setCustomPoseImage}
                  setIsLimitModalOpen={setIsLimitModalOpen}
                  setErrorText={setErrorText}
                  charImage={charImage}
                  setCharImage={setCharImage}
                  recentCharacters={recentCharacters}
                  setIsErrorModalOpen={setIsErrorModalOpen}
                  characterSuggestion={characterSuggestion}
                  setCharacterSuggestion={setCharacterSuggestion}
                  locationSuggestion={locationSuggestion}
                  setLocationSuggestion={setLocationSuggestion}
                  actionSuggestion={actionSuggestion}
                  setActionSuggestion={setActionSuggestion}
                  stickMode={stickMode}
                  setStickMode={setStickMode}
                  emotion={emotion}
                  setEmotion={setEmotion}
                  characterVersion={characterVersion}
                  setCharacterVersion={setCharacterVersion}
                  banned={banned}
                  folderSetting={folderSetting}
                  setFolderSetting={setFolderSetting}
                  folders={folders}
                  setFolders={setFolders}
                  autoSave={autoSave}
                  setAutoSave={setAutoSave}
                  age={age}
                  setAge={setAge}
                  showAttributes={showAttributes}
                  setShowAttributes={setShowAttributes}
                  weight={weight}
                  setWeight={setWeight}
                  breasts={breasts}
                  setBreasts={setBreasts}
                  ass={ass}
                  setAss={setAss}
                  penis={penis}
                  setPenis={setPenis}
                  nsfwEnabled={nsfwEnabled}
                  setNsfwEnabled={setNsfwEnabled}
                  visibilityMode={visibilityMode}
                  setVisibilityMode={setVisibilityMode}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  setBookmarkModalOpen={setBookmarkModalOpen}
                  bookmarkModalOpen={bookmarkModalOpen}
                />
              ) : (
                <InputsVideo
                  model={videoModel}
                  setModel={setVideoModel}
                  imageSize={videoImageSize}
                  setImageSize={setVideoImageSize}
                  quality={quality}
                  setQuality={setQuality}
                  creativity={videoCreativity}
                  setCreativity={setVideoCreativity}
                  prompt={videoPrompt}
                  setPrompt={setVideoPrompt}
                  negativePrompt={videoNegativePrompt}
                  setNegativePrompt={setVideoNegativePrompt}
                  seed={seedVideo}
                  setSeed={setSeedVideo}
                  handleSubmitVideoV3={handleSubmitVideoV3}
                  subscription={subscription}
                  isLoading={videoIsLoading}
                  lora={videoLora}
                  setLora={setVideoLora}
                  locked={locked}
                  setLocked={setLocked}
                  ntier={ntier}
                  setNtier={setNtier}
                  faceFix={faceFix}
                  setFaceFix={setFaceFix}
                  chillMode={chillMode}
                  setChillMode={setChillMode}
                  isAdmin={user?.uid === process.env.NEXT_PUBLIC_ADMIN_UID}
                  safeImage={safeImage}
                  setSafeImage={setSafeImage}
                  watermark={watermark}
                  setWatermark={setWatermark}
                  cooldown={cooldown}
                  cooldownProgress={cooldownProgress}
                  setImageSrc={setImageSrc}
                  setImageHistory={setImageHistory}
                  filter={videoFilter}
                  setFilter={setVideoFilter}
                  details={details}
                  setDetails={setDetails}
                  customPoseImage={customPoseImage}
                  setCustomPoseImage={setCustomPoseImage}
                  setIsLimitModalOpen={setIsLimitModalOpen}
                  setErrorText={setErrorText}
                  charImage={charImageVideo}
                  setCharImage={setCharImageVideo}
                  recentCharacters={recentCharacters}
                  setIsErrorModalOpen={setIsErrorModalOpen}
                  animation={animation}
                  setAnimation={setAnimation}
                  cameraMotion={cameraMotion}
                  setCameraMotion={setCameraMotion}
                  videoRefImage={videoRefImage}
                  setVideoRefImage={setVideoRefImage}
                  setVideoRefLora={setVideoRefLora}
                  setVideoId={setVideoId}
                  videoVersion={videoVersion}
                  setVideoVersion={setVideoVersion}
                  videoModelMode={videoModelMode}
                  setVideoModelMode={setVideoModelMode}
                  dialogue={dialogue}
                  setDialogue={setDialogue}
                  dialogueSpeaker={dialogueSpeaker}
                  setDialogueSpeaker={setDialogueSpeaker}
                  dialogueTone={dialogueTone}
                  setDialogueTone={setDialogueTone}
                  dialogueAccent={dialogueAccent}
                  setDialogueAccent={setDialogueAccent}
                  videoLength={videoLength}
                  setVideoLength={setVideoLength}
                  videoFps={videoFps}
                  setVideoFps={setVideoFps}
                  videoResolution={videoResolution}
                  setVideoResolution={setVideoResolution}
                  cameraMove={cameraMove}
                  setCameraMove={setCameraMove}
                  videoQuality={videoQuality}
                  setVideoQuality={setVideoQuality}
                  videoImageSizeV2={videoImageSizeV2}
                  setVideoImageSizeV2={setVideoImageSizeV2}
                  videoProgress={videoProgress}
                  queueLength={queueLength}
                  setQueueLength={setQueueLength}
                  age={ageVideo}
                  setAge={setAgeVideo}
                  weight={weightVideo}
                  setWeight={setWeightVideo}
                  breasts={breastsVideo}
                  setBreasts={setBreastsVideo}
                  ass={assVideo}
                  setAss={setAssVideo}
                  showAttributes={showAttributesVideo}
                  setShowAttributes={setShowAttributesVideo}
                  videoFramerateV2={videoFramerateV2}
                  setVideoFramerateV2={setVideoFramerateV2}
                  loop={loop}
                  setLoop={setLoop}
                  country={country}
                  animateMode={animateMode}
                  setAnimateMode={setAnimateMode}
                  audioEnabled={audioEnabled}
                  setAudioEnabled={setAudioEnabled}
                  animateVersion={animateVersion}
                  setAnimateVersion={setAnimateVersion}
                  setVideoRefId={setVideoRefId}
                  setVideoSrc={setVideoSrc}
                  videoStartingPoint={videoStartingPoint}
                  setVideoStartingPoint={setVideoStartingPoint}
                  setFormatTab={setFormatTab}
                  setExtendModalOpen={setExtendModalOpen}
                  visibilityMode={visibilityMode}
                  setVisibilityMode={setVisibilityMode}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  lorasList={lorasListVideo}
                  setLorasList={setLorasListVideo}
                  setBookmarkModalOpen={setBookmarkModalOpen}
                  bookmarkModalOpen={bookmarkModalOpen}
                  banned={banned}
                  folderSetting={folderSetting}
                  setFolderSetting={setFolderSetting}
                  folders={folders}
                  setFolders={setFolders}
                  autoSave={autoSave}
                  setAutoSave={setAutoSave}
                />
              )}
            </div>
            <div className="flex flex-col lg:w-2/3 order-2 md:order-2">
              {user &&
                (() => {
                  const showBanner = Boolean(busy || hasUnsavedChanges)
                  const isBusy = Boolean(busy)

                  const handleClick = (e) => {
                    if (!showBanner) {
                      e.preventDefault()
                      return
                    }
                    if (isBusy) return

                    e.preventDefault()
                    window.scrollTo({
                      top: document.body.scrollHeight,
                      behavior: 'smooth',
                    })
                  }

                  return (
                    <div
                      className="mb-2 md:mb-1 flex justify-center"
                      aria-live="polite"
                    >
                      <Link
                        href={isBusy ? '/upgrade' : '/'}
                        scroll={false}
                        onClick={handleClick}
                        className={`
            group
            flex items-center gap-1.5 md:gap-3
            rounded-full
            px-2.5 py-1 md:px-4 md:py-2
            text-xs md:text-sm
            bg-[var(--middle-bg-color)]/90 backdrop-blur
            border border-white/10 shadow-lg
            text-white
            transition-all duration-200 ease-out
            hover:-translate-y-0.5 hover:shadow-xl hover:bg-[var(--middle-bg-color)]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40
            ${
              showBanner
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }
          `}
                      >
                        <span className="text-sm md:text-base leading-none">
                          {isBusy ? '🕚' : '⚠️'}
                        </span>

                        <span className="font-medium opacity-90">
                          {isBusy
                            ? t('waitinginfreequeue')
                            : t('unsavedChanges')}
                        </span>

                        {isBusy && (
                          <span className="hidden md:inline ml-1 font-semibold underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
                            {t('upgradenowtoskipthewait')}
                          </span>
                        )}

                        <span
                          className="ml-0.5 md:ml-1 opacity-60 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0.5"
                          aria-hidden
                        >
                          →
                        </span>
                      </Link>
                    </div>
                  )
                })()}

              <div className="w-full rounded-md relative flex items-center justify-center overflow-auto mt-1 md:mt-0">
                {earlyAccessModal}
                <Modal
                  isOpen={isAlertModalOpen}
                  closeModal={() => setIsAlertModalOpen(false)}
                  title={t('workflowSaved')}
                  content={
                    <>
                      <img
                        src="assets/create/bookmark.webp"
                        alt="Promptchan bookmark example"
                        className="mx-auto md:w-1/2 w-full mb-12"
                      />

                      <p>{t('workflowSavedDescription')}</p>
                    </>
                  }
                  confirmation2={t('openWorkflows')}
                  onConfirm2={() => setBookmarkModalOpen(true)}
                  confirmation={t('done')}
                  onConfirm={() => setIsAlertModalOpen(false)}
                />
                <Modal
                  isOpen={isErrorModalOpen}
                  closeModal={() => setIsErrorModalOpen(false)}
                  title={t('error')}
                  content={errorText || t('somethingWentWrong')}
                  confirmation={t('done')}
                  onConfirm={() => setIsErrorModalOpen(false)}
                />
                {warmingModal}
                <Modal
                  isOpen={publishModalOpen}
                  closeModal={() => {
                    if (isPublishing) return
                    setPublishModalOpen(false)
                  }}
                  title={t('publish')}
                  content={
                    <div>
                      <p className="mb-3 text-base">{t('publishToA')}</p>

                      <div className="mb-4">
                        <label className="block text-xs font-semibold mb-1.5 opacity-90">
                          <span className="inline-flex items-center gap-2">
                            <FaFolder />
                            {t('folder')}
                          </span>
                        </label>

                        <div className="relative">
                          <select
                            disabled={isPublishing}
                            value={selectedFolder}
                            onChange={(e) => setSelectedFolder(e.target.value)}
                            className="w-full rounded-lg border border-white/20 bg-[#0F0D14] px-3 py-2.5 text-white appearance-none focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <optgroup label={t('publicFolders')}>
                              {pubFolders.map((f, idx) => (
                                <option
                                  key={`pub-${f}-${idx}`}
                                  value={`pub:${f}`}
                                >
                                  {f === 'Gallery' ? t('publicGallery') : f}
                                </option>
                              ))}
                            </optgroup>

                            <optgroup label={t('privateFolders')}>
                              {privFolders.map((f, idx) => (
                                <option
                                  key={`priv-${f}-${idx}`}
                                  value={`priv:${f}`}
                                >
                                  {f === 'Gallery' ? t('privateGallery') : f}
                                </option>
                              ))}
                            </optgroup>
                          </select>

                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 opacity-60">
                            ⌄
                          </span>
                        </div>
                      </div>

                      <div
                        className={`mb-4 rounded-md p-2 transition ${
                          showTermsError
                            ? 'ring-1 ring-red-500/70 bg-red-500/5'
                            : ''
                        }`}
                      >
                        <label className="flex items-start gap-2 text-xs text-white cursor-pointer">
                          <input
                            type="checkbox"
                            disabled={isPublishing}
                            checked={acceptedPublishTerms}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setAcceptedPublishTerms(checked)
                              if (checked) setShowTermsError(false)
                            }}
                            className="mt-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                          />

                          <span className="leading-relaxed">
                            {t('publishTermsPrefix')}{' '}
                            <a
                              href="/termsofservice"
                              className="underline underline-offset-2 hover:text-white"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t('termsOfService')}
                            </a>{' '}
                            {t('publishTermsSuffix')}
                          </span>
                        </label>

                        {showTermsError && (
                          <p className="mt-1 text-xs text-red-400">
                            {t('mustAcceptTerms')}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          disabled={isPublishing}
                          onClick={async () => {
                            if (isPublishing) return

                            if (!acceptedPublishTerms) {
                              setShowTermsError(true)
                              return
                            }

                            setShowTermsError(false)
                            await publishContent()
                          }}
                          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white
                        bg-[var(--foreground-bg-color)] transition-transform duration-200 hover:scale-[1.05]
                        disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 width-32"
                        >
                          {isPublishing ? (
                            <>
                              <span
                                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
                                aria-hidden="true"
                              />
                              {t('publishing')}
                            </>
                          ) : (
                            <>
                              {(() => {
                                const [scope] = (
                                  selectedFolder || 'pub:Gallery'
                                ).split(':')
                                return scope === 'pub' ? (
                                  <FaGlobeAmericas />
                                ) : (
                                  <FaLock />
                                )
                              })()}
                              {t('publish')}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  }
                />
                <Modal
                  isOpen={isSessionModalOpen}
                  closeModal={() => setIsSessionModalOpen(false)}
                  title={t('sessionExpired')}
                  content={t('sessionExpiredDescription')}
                  confirmation={t('refresh')}
                  onConfirm={async () => {
                    try {
                      await getIdToken(user, true)
                    } catch {}

                    if (typeof window !== 'undefined') {
                      window.location.reload()
                    }
                  }}
                />
                <Modal
                  isOpen={isLimitModalOpen}
                  closeModal={() => setIsLimitModalOpen(false)}
                  title={limitTitle || `🚀 ${t('upgradetocontinuegenerating')}`}
                  content={errorText || t('somethingWentWrong')}
                  confirmation={t('upgradeandunlockbenefits')}
                  onConfirm={() => {
                    subscription.plan === 'Pro' ||
                    subscription.plan === 'Studio'
                      ? router.push('/gems')
                      : router.push('/upgrade')
                  }}
                />
                <Modal
                  isOpen={xlLimitModalOpen}
                  closeModal={() => setXlLimitModalOpen(false)}
                  title={`${t('upgradeToAccess')} 🔥`}
                  content={
                    <div className="text-center">
                      <Link href="/upgrade">
                        <img
                          src="/assets/modals/xl.webp"
                          alt="Upgrade for XL"
                          className="rounded-xl w-2/3 mx-auto mb-2"
                        />
                      </Link>
                      <p>{t('youveRunOut')}</p>
                    </div>
                  }
                  confirmation={t('upgradeandunlockbenefits')}
                  onConfirm={() => {
                    subscription.plan === 'Pro' ||
                    subscription.plan === 'Studio'
                      ? router.push('/gems')
                      : router.push('/upgrade')
                  }}
                  confirmation2={t('useSdStyles')}
                  onConfirm2={() => {
                    setModel('Real')
                  }}
                />
                <TooltipModal
                  isOpen={tooltipModalOpen}
                  closeModal={() => setTooltipModalOpen(false)}
                  title={t('welcomeToCreate')}
                  content={t('welcomeToCreateDescription')}
                  confirmation={t('startCreating')}
                  onConfirm={() => setTooltipModalOpen(false)}
                />
                <ExtendModal
                  isOpen={extendModalOpen}
                  closeModal={() => setExtendModalOpen(false)}
                  subscription={subscription}
                  lorasList={null}
                  setLorasList={null}
                  model={lastVideoModel ?? videoModel}
                  setIsLimitModalOpen={setIsLimitModalOpen}
                  setErrorText={setErrorText}
                  setIsErrorModalOpen={setIsErrorModalOpen}
                  user={user}
                  handleSubmitVideoV3={handleSubmitVideoV3}
                  extendId={extendVideoId ?? videoDocRef[extendSourceIndex]}
                  sourceVersion={extendSourceVersion}
                />
                <div
                  className={`items-center md:grid-cols-2 transition-all duration-300 ${
                    imageSrc && imageSrc.length > 1 && imageSize !== 'Wide'
                      ? 'grid gap-4 overflow-auto'
                      : 'flex flex-col justify-center'
                  } `}
                >
                  {formatTab === 'photo' && imageSrc.length > 0 ? (
                    imageSrc.map((img, index) => {
                      return img ? (
                        // ✅ make this the hover group + keep relative for overlay
                        <div className="relative group" key={index}>
                          {/* Media */}
                          {isUrl(img) ? (
                            <img
                              key={img}
                              src={img.replace(
                                process.env.NEXT_PUBLIC_URL_1,
                                process.env.NEXT_PUBLIC_URL_2,
                              )}
                              alt="Generated"
                              // Don't reveal a paywalled (blurred) image full-screen unblurred.
                              onClick={
                                blurryImage
                                  ? undefined
                                  : () =>
                                      setFullscreenImage(
                                        img.replace(
                                          process.env.NEXT_PUBLIC_URL_1,
                                          process.env.NEXT_PUBLIC_URL_2,
                                        ),
                                      )
                              }
                              className={`object-cover rounded-2xl fade-in-out max-h-screen ${
                                blurryImage ? '' : 'cursor-pointer'
                              } ${
                                blurryImage && !user
                                  ? 'blur-2xl'
                                  : blurryImage
                                    ? 'blur-md'
                                    : ''
                              }`}
                            />
                          ) : blurryImage ? (
                            <Link href={'/upgrade'}>
                              <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                                <FaLock className="text-white text-4xl opacity-40" />
                                <p className="text-white font-semibold text-lg mt-2 opacity-80">
                                  {t('upgradetoview')}
                                </p>
                              </div>
                              <div
                                className={`${!user ? 'blur-3xl' : 'blur-md'}`}
                              >
                                <img
                                  key={img}
                                  src={`data:image/jpeg;base64,${img}`}
                                  alt="Generated"
                                  className="object-cover rounded-2xl fade-in-out z-0 max-h-screen"
                                />
                              </div>
                            </Link>
                          ) : (
                            <img
                              key={img}
                              src={`data:image/jpeg;base64,${img}`}
                              alt="Generated"
                              onClick={() =>
                                setFullscreenImage(
                                  `data:image/jpeg;base64,${img}`,
                                )
                              }
                              className="object-cover rounded-2xl fade-in-out z-0 max-h-screen cursor-pointer"
                            />
                          )}

                          {/* ✅ Controls OVERLAY (appear on hover) */}
                          {!blurryImage && (
                            <div
                              className={`pointer-events-none absolute inset-0 z-20 flex items-start justify-end p-3 transition-opacity duration-200 ${
                                openMenuIndex === index
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <div className="pointer-events-auto flex items-center gap-2">
                                {/* Animate (primary) */}
                                <button
                                  type="button"
                                  className={OVERLAY_PILL}
                                  onClick={() => useAnimate(index)}
                                >
                                  <FaFilm className="text-base" />
                                  <span>{t('animate')}</span>
                                </button>

                                {/* Save File (icon only) */}
                                <button
                                  type="button"
                                  title={t('saveFile')}
                                  className={OVERLAY_ICON}
                                  onClick={() => {
                                    if (earlyAccessRef.current[docRef[index]]) {
                                      showEarlyAccessModal()
                                      return
                                    }
                                    setHasUnsavedChanges(false)
                                    isUrl(img)
                                      ? downloadImage(img)
                                      : saveImage(
                                          `data:image/jpeg;base64,${img}`,
                                          `image_${index}.jpg`,
                                        )
                                  }}
                                >
                                  <FaSave className="text-base" />
                                </button>

                                {/* More options */}
                                <OverlayActionsMenu
                                  onOpenChange={(o) =>
                                    setOpenMenuIndex(o ? index : null)
                                  }
                                  items={[
                                    ...(offersPublish({
                                      isUrl: isUrl(img),
                                      docRef: docRef[index],
                                      kept: keptRef.current[docRef[index]],
                                    })
                                      ? [
                                          {
                                            key: 'publish',
                                            icon: (
                                              <FaGlobeAmericas className="text-sm" />
                                            ),
                                            label: 'Publish',
                                            onClick: () => {
                                              setHasUnsavedChanges(false)
                                              setPublishModalOpen(true)
                                              setPublishIndex(index)
                                              setPublishFormat(formatTab)
                                            },
                                          },
                                        ]
                                      : []),
                                    {
                                      key: 'workflow',
                                      icon: (
                                        <FaClipboardList className="text-sm" />
                                      ),
                                      label: 'Save Workflow',
                                      onClick: () => savePrompt(index),
                                    },
                                    {
                                      key: 'delete',
                                      icon: <FaTrash className="text-sm" />,
                                      label: 'Delete',
                                      destructive: true,
                                      loading: isDeleting,
                                      onClick: () => deleteImage(index),
                                    },
                                  ]}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null
                    })
                  ) : formatTab === 'video' && videoSrc.length > 0 ? (
                    videoSrc.map((img, index) => {
                      return img ? (
                        // ✅ hover group for video too
                        <div className="relative group" key={index}>
                          {/* Media */}
                          {isUrl(img) ? (
                            user?.uid ? (
                              <Link
                                href={
                                  img?.includes('temp')
                                    ? '#'
                                    : `/image/${videoDocRef[index]}`
                                }
                                className={`${
                                  blurryImage && !user
                                    ? 'blur-2xl'
                                    : blurryImage
                                      ? 'blur-md'
                                      : ''
                                }`}
                              >
                                <video
                                  key={img}
                                  src={img.replace(
                                    process.env.NEXT_PUBLIC_URL_1,
                                    process.env.NEXT_PUBLIC_URL_2,
                                  )}
                                  className="object-cover rounded-2xl fade-in-out z-0 max-h-screen"
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  controls
                                />
                              </Link>
                            ) : (
                              <video
                                key={img}
                                src={img.replace(
                                  process.env.NEXT_PUBLIC_URL_1,
                                  process.env.NEXT_PUBLIC_URL_2,
                                )}
                                className={`object-cover rounded-2xl fade-in-out z-0 max-h-screen ${
                                  blurryImage && !user
                                    ? 'blur-2xl'
                                    : blurryImage
                                      ? 'blur-md'
                                      : ''
                                }`}
                                autoPlay="autoplay"
                                loop={true}
                                muted={true}
                                playsInline
                              />
                            )
                          ) : blurryImage ? (
                            <Link href="/upgrade">
                              <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                                <FaLock className="text-white text-4xl opacity-40" />
                                <p className="text-white font-semibold text-lg mt-2 opacity-80">
                                  {t('upgradetoview')}
                                </p>
                              </div>
                              <video
                                key={img}
                                src={img.replace(
                                  process.env.NEXT_PUBLIC_URL_1,
                                  process.env.NEXT_PUBLIC_URL_2,
                                )}
                                className="object-cover rounded-2xl fade-in-out z-0"
                                autoPlay="autoplay"
                                loop={true}
                                muted={true}
                                playsInline
                              />
                            </Link>
                          ) : (
                            <video
                              key={img}
                              src={img.replace(
                                process.env.NEXT_PUBLIC_URL_1,
                                process.env.NEXT_PUBLIC_URL_2,
                              )}
                              className="object-cover rounded-2xl fade-in-out z-0"
                              autoPlay="autoplay"
                              loop={true}
                              muted={true}
                              playsInline
                            />
                          )}

                          {/* ✅ Publish + Extend overlay on hover */}
                          {!blurryImage && img?.includes('temp') && (
                            <div
                              className={`pointer-events-none absolute inset-0 z-20 flex items-start justify-end p-3 transition-opacity duration-200 ${
                                openMenuIndex === index
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <div className="pointer-events-auto flex items-center gap-2">
                                {/* Extend (primary). All versions — v5 has its own extend
                                    pipeline; the backend branches on the source's version. */}
                                {supportsExtend(videoVersion) && (
                                  <button
                                    type="button"
                                    disabled={videoIsLoading}
                                    className={`${OVERLAY_PILL} relative`}
                                    onClick={() => {
                                      setExtendVideoId(null)
                                      setExtendSourceIndex(index)
                                      setExtendModalOpen(true)
                                    }}
                                  >
                                    <FaAngleDoubleRight className="text-base" />
                                    <span>{t('extend')}</span>
                                    <span className="pointer-events-none absolute -top-2 -right-1 bg-[var(--main-bg-color)] text-white text-[9px] font-bold leading-none px-1.5 py-0.5 rounded-full shadow-lg ring-1 ring-white/40">
                                      {t('new')}
                                    </span>
                                  </button>
                                )}

                                {/* Save File (icon only) */}
                                <button
                                  type="button"
                                  title={t('saveFile')}
                                  className={OVERLAY_ICON}
                                  onClick={() =>
                                    downloadVideo(
                                      img.replace(
                                        process.env.NEXT_PUBLIC_URL_1,
                                        process.env.NEXT_PUBLIC_URL_2,
                                      ),
                                    )
                                  }
                                >
                                  <FaSave className="text-base" />
                                </button>

                                {/* More options */}
                                <OverlayActionsMenu
                                  onOpenChange={(o) =>
                                    setOpenMenuIndex(o ? index : null)
                                  }
                                  items={[
                                    {
                                      key: 'publish',
                                      icon: (
                                        <FaGlobeAmericas className="text-sm" />
                                      ),
                                      label: 'Publish',
                                      onClick: () => {
                                        setHasUnsavedChanges(false)
                                        setPublishModalOpen(true)
                                        setPublishIndex(index)
                                        setPublishFormat(formatTab)
                                      },
                                    },
                                    {
                                      key: 'delete',
                                      icon: <FaTrash className="text-sm" />,
                                      label: 'Delete',
                                      destructive: true,
                                      loading: isDeleting,
                                      onClick: () => deleteVideo(index),
                                    },
                                  ]}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center px-4 py-6">
                      <div className="flex items-center gap-2 mb-2">
                        {formatTab === 'video' ? (
                          <FaVideo size={24} className="text-gray-400" />
                        ) : (
                          <FaImage size={24} className="text-gray-400" />
                        )}

                        <h1 className="text-white text-2xl font-semibold tracking-tight">
                          {formatTab === 'video' ? t('video') : t('image')}
                        </h1>
                      </div>

                      <h3 className="text-gray-400 text-sm leading-relaxed max-w-md">
                        {formatTab === 'video'
                          ? t('enterAPromptAndYourDesiredVideo')
                          : t('enterAPromptAndYourDesired')}
                      </h3>

                      {/* Transitional: nudges legacy opt-outs (Settings toggle)
                          back to the new UI; switching clears the cookie so SSR
                          re-picks Variant B on refresh. Remove with the legacy
                          branch. */}
                      <p className="mt-5 text-xs leading-relaxed text-gray-500 max-w-sm">
                        {t('newCreateUiBannerText')}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setLegacyGenerateUi(false)
                          router.refresh()
                        }}
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-300 transition hover:text-indigo-200"
                      >
                        <FaMagic size={12} />
                        {t('newCreateUiBannerCta')}
                        <FaLongArrowAltRight size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {formatTab === 'video' ? (
                <div className="mt-8 flex flex-col items-center justify-center">
                  {videoQuery?.length > 0 ? (
                    <div className="relative flex items-center w-2/3 mx-auto">
                      <div className="flex-grow border-t border-gray-800" />

                      {/* CENTER GROUP */}
                      <div className="mx-5 flex flex-col sm:flex-row items-center gap-3">
                        {videoHistoryVisible && (
                          <button
                            type="button"
                            onClick={() => {
                              downloadAllVideos(videoQuery)
                              setHasUnsavedChanges(false)
                            }}
                            className="rounded-full px-3 py-1.5 text-xs text-white font-semibold flex items-center justify-center gap-1.5 transition hover:scale-[1.05] bg-[var(--main-bg-color)]"
                          >
                            <FaDownload />
                            Save All
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setVideoHistoryVisible(!videoHistoryVisible)
                          }
                          className="rounded-full px-3 py-1.5 text-xs text-white font-semibold flex items-center justify-center gap-1.5 transition hover:scale-[1.05] bg-[var(--foreground-bg-color)]"
                        >
                          <TbHistoryToggle />
                          {videoHistoryVisible ? 'Hide' : 'Show'} History
                        </button>
                      </div>

                      <div className="flex-grow border-t border-gray-800" />
                    </div>
                  ) : null}

                  {videoHistoryVisible && (
                    <>
                      <div className="my-6 flex align-center items-center mx-auto space-x-2">
                        <button onClick={getVideoQuery}>
                          <div className="mx-auto w-32 py-3 bg-[var(--middle-bg-color)] text-white rounded-full text-center flex justify-center items-center font-semibold">
                            <HiRefresh className="mr-2 text-[var(--main-bg-color)]" />
                            Refresh
                          </div>
                        </button>
                      </div>
                      <p className="text-xs text-gray-300 text-center">
                        View your video progress from the last 24 hours. Videos
                        are only saved for 24 hours (unless published). Remember
                        to download!
                      </p>
                      <p className="mt-2 flex items-start justify-center gap-1.5 text-center text-xs text-gray-400">
                        <FaGem
                          className="mt-[2px] shrink-0 text-[var(--main-bg-color)]"
                          size={11}
                        />
                        <span>{t('videoRefundNote')}</span>
                      </p>
                    </>
                  )}

                  {videoQueryLoading ? (
                    <div className="mt-12">
                      <ClipLoader
                        color={'#FFF'}
                        loading={videoQueryLoading}
                        size={24}
                        aria-label={t('loadingSpinner')}
                        data-testid="loader"
                      />
                    </div>
                  ) : videoQuery?.length > 0 && videoHistoryVisible ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-12 flex-wrap self-center items-center">
                      {videoQuery.map((video, index) => {
                        const isDone =
                          video.status === 'Successful' &&
                          video.final_response?.video?.length > 0
                        const isProcessing =
                          video.status === 'Submitted' ||
                          (!isDone &&
                            video.status !== 'Failed' &&
                            video.status !== 'refund_claimable')
                        const isFailed =
                          !isDone &&
                          video.status === 'Failed' &&
                          video.refund_status === 'Claimed'
                        const isRefundClaimable =
                          video?.refund_claimable === true
                        const progress = Math.round(
                          (video.percentage_complete || 0) * 100,
                        )
                        const videoUrl = video.final_response?.video?.[0]
                        const thumbnail =
                          video.metadata?.base_image_url ??
                          '/assets/models/random.webp'
                        const prompt = video.metadata?.prompt?.trim() || 'Auto'

                        return (
                          <div
                            key={video._id}
                            className="relative rounded-lg overflow-hidden shadow-md border-2 border-[var(--secondary-bg-color)] fade-in-out"
                          >
                            <img
                              src={thumbnail}
                              alt="Base image"
                              className="w-full h-48 object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent text-white text-sm p-2">
                              <p className="font-medium">
                                <span className="text-white">
                                  {prompt
                                    ? truncate(prompt, 18)
                                    : `Video #${index + 1}`}
                                </span>
                              </p>

                              <p className="text-xs text-gray-100">
                                {timeAgo(video.created_at)}
                              </p>

                              <div className="flex flex-col items-center space-y-1">
                                {isDone && (
                                  <a
                                    href={videoUrl}
                                    className="text-white bg-green-400/50 hover:bg-green-300/50 shadow-md rounded-full flex items-center py-1 px-2 mt-2 font-semibold text-xs"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <FaCheck className="mr-2" /> View Video
                                  </a>
                                )}

                                {isRefundClaimable ? (
                                  <button
                                    onClick={() => refundGems(video._id)}
                                    className="text-white bg-orange-400/50 hover:bg-orange-300/50 shadow-md rounded-full flex items-center py-1 px-2 mt-2 font-semibold text-xs"
                                    disabled={refundLoading}
                                  >
                                    <FaTimes className="mr-2" /> Refund Gems
                                  </button>
                                ) : isProcessing ? (
                                  <p className="text-yellow-400 font-semibold text-xs">
                                    Processing... {progress}%
                                  </p>
                                ) : null}

                                {isFailed && (
                                  <p className="text-white bg-green-300/50 shadow-md rounded-full flex items-center py-1 px-2 mt-2 font-semibold text-xs">
                                    <FaCheck className="mr-2" /> Refunded
                                  </p>
                                )}

                                {!isDone &&
                                  !isProcessing &&
                                  !isFailed &&
                                  !isRefundClaimable && (
                                    <p className="text-red-400 font-semibold text-xs">
                                      ❓ Status — {video.status}
                                    </p>
                                  )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p></p>
                  )}
                </div>
              ) : (
                <div className="mt-8 flex flex-col items-center justify-center">
                  {imageHistory?.length > 0 ? (
                    <div className="relative flex items-center w-2/3 mx-auto">
                      <div className="flex-grow border-t border-gray-800" />

                      {/* CENTER GROUP */}
                      <div className="mx-5 flex flex-col sm:flex-row items-center gap-3">
                        {formatTab === 'photo' &&
                          sessionHistoryVisible &&
                          imageHistory.some(
                            (img) =>
                              !isUrl(img) || unsavedAddressRef.current.has(img),
                          ) && (
                            <button
                              type="button"
                              onClick={() => {
                                downloadAllImages(imageHistory)
                                setHasUnsavedChanges(false)
                              }}
                              className="rounded-full px-3 py-1.5 text-xs text-white font-semibold flex items-center justify-center gap-1.5 transition hover:scale-[1.05] bg-[var(--main-bg-color)]"
                            >
                              <FaDownload />
                              Save All
                            </button>
                          )}

                        <button
                          type="button"
                          onClick={() =>
                            setSessionHistoryVisible(!sessionHistoryVisible)
                          }
                          className="rounded-full px-3 py-1.5 text-xs text-white font-semibold flex items-center justify-center gap-1.5 transition hover:scale-[1.05] bg-[var(--foreground-bg-color)]"
                        >
                          <FaHistory />
                          {sessionHistoryVisible ? 'Hide' : 'Show'} History
                        </button>
                      </div>

                      <div className="flex-grow border-t border-gray-800" />
                    </div>
                  ) : null}

                  {formatTab === 'photo' && sessionHistoryVisible && (
                    <>
                      <div ref={imageRef} />

                      <div className="md:w-2/3 mx-auto">
                        {imageHistory?.length > 0 && (
                          <ImageGrid
                            imageHistory={imageHistory}
                            user={user}
                            blurryImage={blurryImage}
                            loading="lazy"
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {!user && (
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-3xl text-white font-semibold mt-16">
            <span className="text-[var(--main-bg-color)]">{t('create')}</span>{' '}
            {t('yourFantasy')}
          </h2>
          <h3 className="text-gray-300 mb-6 mt-1">
            {t('createFeaturesDescription')}
          </h3>

          <Link href="/signup">
            <img
              src="/assets/create/create.webp"
              className="rounded-xl md:w-2/3 mx-auto"
            />
          </Link>

          <div className="flex justify-center">
            <div className="w-2/3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-[var(--background-bg-color)]">
                {features.map((tip, index) => (
                  <div
                    key={index}
                    className="bg-[var(--foreground-bg-color)] shadow-lg rounded-xl p-8"
                  >
                    <div className="flex items-center justify-center mb-2">
                      <div className="text-[var(--main-bg-color)] text-xl mr-2">
                        {tip.icon}
                      </div>
                      <h2 className="text-lg font-bold text-white">
                        {tip.title}
                      </h2>
                    </div>
                    <p className="text-gray-300 text-sm">{tip.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Link href="/signup">
            <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 mb-36 mt-10 text-white rounded-3xl focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.03]">
              Get Started <FaLongArrowAltRight className="ml-2" />
            </div>
          </Link>
        </div>
      )}
    </div>
  )
}

export default Create
